// KineDB client — JSON over HTTP, binary TLV over WebSocket.
//
// The server (kinedb-server, default :4820) exposes:
//
//   POST /sql       body = raw SQL text                    → Response JSON
//   GET  /health                                           → Response::Health (JSON)
//   GET  /ws        WebSocket upgrade, BINARY TLV frames   → Request/Response
//
// The HTTP endpoints still speak JSON. The WebSocket transport is binary-only:
// each WS frame carries a single Request/Response encoded as a Frame
// (`[msg_type:1][flags:1][request_id:2 BE][payload_length:4 BE]` + TLV body).
// See `crates/kinedb-protocol/src/{frame,tlv,codec,messages}.rs` for the
// canonical Rust implementation this file mirrors.
//
// Works in browser, Bun, and Node 22+ (all have global fetch + WebSocket).
//
// Usage:
//   import { KineDB } from './kinedb.js';
//   const db = new KineDB();                              // defaults to current origin (Vite proxy in dev)
//   const db = new KineDB('http://localhost:4820');       // explicit
//   const db = new KineDB(url, undefined, { retry: true }); // absorb server-flagged
//     transient rejections (error with `retry:true`, backoff) with a random
//     full-jitter wait — see the KineDB constructor JSDoc for the knobs
//   const r  = await db.sql('SELECT 1');                  // one-shot HTTP
//   const h  = await db.health();
//   const ws = await db.connect();                        // persistent WS
//   const r  = await ws.sql('SELECT * FROM users');
//   const sub = await ws.watch('users', (n) => console.log(n));
//   sub.cancel();
//   ws.close();

// ── files (B971) ────────────────────────────────────────────────────
//
// A file rides the wire as content-defined chunks. `./chunker.js` splits the
// bytes and `./blake3.js` addresses each chunk. Both re-export from here, so a
// consumer of `@kinedb/client` reaches them without a deep import.
export {
  AVG_CHUNK_BYTES,
  GEAR_SEED,
  MASK_BITS,
  MAX_CHUNK_BYTES,
  MIN_CHUNK_BYTES,
  chunkBoundaries,
  chunks,
  gearTable,
} from './chunker.js';
export { Hasher as Blake3Hasher, blake3, hashHex as blake3Hex } from './blake3.js';
// B2171: the typed parameters of `sql(text, params)`.
// B2174: one complete transaction in one frame, `transaction(statements)`.
export {
  MAX_PARAMS,
  MAX_TXN_STATEMENTS,
  SQL_PARAMS_CONTENT_TYPE,
  SQL_TXN_CONTENT_TYPE,
  encodeParams,
  encodeSqlParamsBody,
  encodeTransactionPayload,
  encodeTypedParams,
} from './params.js';
import {
  SQL_PARAMS_CONTENT_TYPE,
  SQL_TXN_CONTENT_TYPE,
  encodeParams,
  encodeTransactionPayload,
  sqlParamsBodyOf,
} from './params.js';
// B2173: `call(name, args)`, the typed function call.
import { FunctionCalls } from './call.js';
// A re-export does not bring a name into THIS module's scope, and `putFile`
// needs both of them, so import them as well.
import { chunks as cdcChunks } from './chunker.js';
import { blake3 as blake3Hash, hashHex as blake3HashHex } from './blake3.js';

// ── the credential seam (B836) ──────────────────────────────────────
//
// B05 slice 7 imported `getToken` / `onUnauthorized` straight from the console's
// `auth-store.js`, and accepted the import CYCLE that created. This file is now
// the package `@kinedb/client`, and a package must not know the session store of
// one application. So the dependency is inverted: the app installs the two hooks,
// the client calls them.
//
// The property that import bought is KEPT. This file is the single HTTP
// transport, so one installed hook still covers every caller in the app, and no
// `new KineDB(...)` call site has to carry a credential.
//
//   setAuthHooks({ getToken, onUnauthorized })   — once, at app start
//   new KineDB(url, ws, { getToken, onUnauthorized })  — per instance, wins
//
// With neither, the client sends no bearer and bounces nobody on a 401. That is
// what a consumer who never logs in wants.
let _hooks = { getToken: null, onUnauthorized: null };

/**
 * Install the app-wide credential hooks. Call once, before the first request.
 *
 * @param {{getToken?: (() => string|null)|null,
 *          onUnauthorized?: ((refusedToken: string|null) => void)|null}} [hooks]
 *   - `getToken` is read on EVERY request, never cached, so a login or a logout
 *     takes effect on the next call with no client to rebuild.
 *   - `onUnauthorized` runs on a `401` BEFORE the throw, so a caller that
 *     swallows the error still gets logged out instead of retrying for ever. It
 *     receives the token that request sent (`null` for none): a 401 that arrives
 *     after a new login refused the OLD token, and must not log the new one out.
 *   Pass `{}` to uninstall both.
 * @returns {{getToken: (()=>string|null)|null, onUnauthorized: (()=>void)|null}}
 *   the hooks that were in force before this call. Feed it back to
 *   `setAuthHooks` to restore them — a test that overrides the app's hooks, or
 *   a tool that borrows a second credential for one task, needs that.
 */
export function setAuthHooks(hooks = {}) {
  const prev = _hooks;
  _hooks = {
    getToken: typeof hooks.getToken === 'function' ? hooks.getToken : null,
    onUnauthorized: typeof hooks.onUnauthorized === 'function' ? hooks.onUnauthorized : null,
  };
  return prev;
}

/** The hooks in force for one client: its own, else the installed ones. */
function hooksFor(own) {
  return {
    getToken: own?.getToken ?? _hooks.getToken,
    onUnauthorized: own?.onUnauthorized ?? _hooks.onUnauthorized,
  };
}

export class KineDBError extends Error {
  constructor(message, response) {
    // B2304: a response with the refusal kind `outcome_unknown` always gives
    // the subclass, so every site that wraps a server error gets the type.
    if (new.target === KineDBError && isOutcomeUnknown(response)) {
      return new KineDBOutcomeUnknownError(message, response);
    }
    super(message);
    this.name = 'KineDBError';
    this.response = response;
    // The server stamps transient rejections on the wire (`retry: true` on a
    // JSON error, trailing Bool on a TLV error, or a backoff response): the
    // request truthfully did NOT apply and simply re-sending it is lossless.
    // Surfaced here so callers without the built-in retry option can build
    // their own loop without substring-matching server messages.
    this.retryable = !!(response && (response.retry === true || response.type === 'backoff'));
    // B2304: true only on a KineDBOutcomeUnknownError.
    this.outcomeUnknown = false;
  }
}

/**
 * B2304: the server sent the request on, but the reply was lost. The write
 * MAY have applied. The error is terminal (`retryable` is false), and no
 * retry loop of this client sends the request again. Read the data back
 * before you send the request again.
 */
export class KineDBOutcomeUnknownError extends KineDBError {
  constructor(message, response) {
    super(message, response);
    this.name = 'KineDBOutcomeUnknownError';
    this.outcomeUnknown = true;
  }
}

/** B2304: true when `response` is the typed refusal `outcome_unknown`. */
function isOutcomeUnknown(response) {
  return response?.refusal?.kind === 'outcome_unknown';
}

// ── client-side retrying ────────────────────────────────────────────
//
// B2298: with no `retry` option, the client retries ONLY a typed timed
// refusal (`refusal.kind` in TIMED_REFUSAL_KINDS: `write_hold`, `not_ready`).
// The server wrote nothing, so a resend is safe. The resends use the hold
// budget (`holdBudgetMs`). Every other transient rejection throws immediately
// (marked `err.retryable`), and a backoff reply keeps its present shape.
// Pass `{ retry: true }` (or a config object) as the third KineDB constructor
// argument to have the client absorb all of them. Pass `{ retry: false }` to
// turn off every retry, also the default retry of a hold refusal.
// B2304: no loop retries a `retry: false` error. The refusal `outcome_unknown`
// (`KineDBOutcomeUnknownError`) is one: the write may have applied.

// B495: the time budget of the retries on a split write hold refusal. It
// equals the server's PARK_CEILING / HOLD_RETRY_BUDGET (kinedb-protocol).
// Change the two values together.
export const HOLD_RETRY_BUDGET_MS = 20000;

const DEFAULT_RETRY = Object.freeze({
  maxRetries: 5,    // resends after the initial attempt
  baseDelayMs: 50,  // jitter window of the first retry
  maxDelayMs: 2000, // ceiling of the jitter window
  holdBudgetMs: HOLD_RETRY_BUDGET_MS, // B495: time budget of hold refusals, from the first send
});

// B495: the kind codes of a typed refusal, item 2 of a TLV Error payload.
// B2173: code 2 is a stale function signature. It is not retryable: `call()`
// reads the signature again and sends again one time, and no retry loop does.
// B2157: code 3 is the refusal of the boot barrier of a node that starts.
// B2304: code 5 is a terminal error whose write may have applied
// (`KineDBOutcomeUnknownError`). It is not a timed refusal: no loop retries it.
const REFUSAL_KINDS = Object.freeze({
  1: 'write_hold', 2: 'signature', 3: 'not_ready', 5: 'outcome_unknown',
});

// B2157: the refusal kinds that a retry loop waits on, on the hold budget.
const TIMED_REFUSAL_KINDS = new Set(['write_hold', 'not_ready']);

// B2298: `undefined` (no option) gives the default: `holdOnly`, a retry of a
// timed refusal only. `false` or `null` turns off every retry. `true` or an
// object gives the full retry. `connect()` passes a normalized object to the
// socket, so the spread keeps its `holdOnly`.
function normalizeRetryOpts(retry) {
  if (retry === undefined) return { ...DEFAULT_RETRY, holdOnly: true };
  if (!retry) return null;
  return retry === true ? { ...DEFAULT_RETRY } : { ...DEFAULT_RETRY, ...retry };
}

// B2298: true when the `retry` options let a loop retry `resp`. `resp` must
// be a transient reply. A `holdOnly` option retries a timed refusal only.
function retryAllows(retry, resp) {
  if (!retry) return false;
  return !retry.holdOnly || TIMED_REFUSAL_KINDS.has(resp?.refusal?.kind);
}

/**
 * Full-jitter backoff: a RANDOM wait in `[0, min(maxDelayMs, baseDelayMs·2^attempt))`.
 * Random on purpose — a leader flip rejects every in-flight writer at the same
 * instant, and a fixed delay would march them all back in lockstep.
 */
export function retryDelayMs(attempt, { baseDelayMs, maxDelayMs }) {
  return Math.random() * Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
}

/**
 * B495: the next wait of a retry loop, or `null` when the loop must stop and
 * surface `resp`. Call it only for a transient reply.
 *
 * A write hold refusal (`resp.refusal.kind === 'write_hold'`) and a not-ready
 * refusal of a starting node (`'not_ready'`, B2157) do not use an
 * attempt. The wait is the server's `retry_ms`, clamped to `[1, maxDelayMs]`,
 * and it never goes past `startedMs + holdBudgetMs`. Every other transient
 * reply uses one attempt of `maxRetries` and a full-jitter wait, plus the
 * `retry_ms` pacing of a backoff reply.
 *
 * @returns {{delayMs: number, attempt: number} | null} `attempt` is the
 *   attempt count for the next call.
 */
export function nextRetryDelay(resp, attempt, startedMs, nowMs, opts) {
  if (TIMED_REFUSAL_KINDS.has(resp?.refusal?.kind)) {
    const deadline = startedMs + (opts.holdBudgetMs ?? HOLD_RETRY_BUDGET_MS);
    if (nowMs >= deadline) return null;
    const hint = Math.min(Math.max(Number(resp.refusal.retry_ms) || 0, 1), opts.maxDelayMs);
    return { delayMs: Math.min(hint, deadline - nowMs), attempt };
  }
  if (attempt >= opts.maxRetries) return null;
  const pacing = resp?.type === 'backoff' ? Number(resp.retry_ms) || 0 : 0;
  return { delayMs: pacing + retryDelayMs(attempt, opts), attempt: attempt + 1 };
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function joinUrl(base, path) {
  if (!base) return path;
  return base.replace(/\/+$/, '') + path;
}

function unwrap(resp) {
  if (resp && resp.type === 'error') throw new KineDBError(resp.message, resp);
  return resp;
}

// Best-effort: include the server's response body in the error so 500s show
// the actual cause instead of just "HTTP 500".
async function httpErrorMessage(response, path) {
  let body = '';
  try { body = (await response.text()).trim(); } catch { /* discard */ }
  if (body) {
    try {
      const j = JSON.parse(body);
      if (j?.type === 'error' && j.message) return `${path} → HTTP ${response.status}: ${j.message}`;
    } catch { /* not JSON */ }
    return `${path} → HTTP ${response.status}: ${body.slice(0, 240)}`;
  }
  return `${path} → HTTP ${response.status}`;
}

/** `POST /sql` has no branch selector: refuse a branch other than main. */
function refuseHttpBranch(branch) {
  if (branch != null && branch !== 'main') {
    throw new KineDBError(
      `POST /sql has no branch selector, so the default branch ${JSON.stringify(branch)} `
      + 'cannot apply: use connect(), or name the branch in the table name (db@branch.table)');
  }
}

export class KineDB {
  /**
   * @param {string} [baseUrl] - origin of the kinedb-server. When omitted, the
   *   client derives one from the page's URL (production / behind a reverse
   *   proxy) or returns `''` so requests pass through the Vite dev proxy.
   *   See `defaultBaseUrl()` for the full rules.
   * @param {string} [wsUrl]   - explicit WebSocket URL (e.g. `wss://host/ws`).
   *   When omitted, derived from the page URL the same way — see
   *   `defaultWsUrl()`. Reverse proxies that terminate TLS get `wss://`
   *   automatically because we read `window.location.protocol`.
   * @param {{retry?: boolean|{maxRetries?:number, baseDelayMs?:number, maxDelayMs?:number,
   *                           holdBudgetMs?:number},
   *          getToken?: () => string|null,
   *          onUnauthorized?: () => void}} [opts]
   *   - `retry`: absorb server-flagged transient rejections (`retry: true` on
   *   an error, or a backoff response) by waiting a random full-jitter delay
   *   and re-sending, up to `maxRetries` times. A split write hold refusal
   *   (`refusal.kind === 'write_hold'`) is re-sent after the server's
   *   `retry_ms` until `holdBudgetMs` from the first send, and does not use
   *   `maxRetries` (B495). Default (no `retry` option, B2298): only a typed
   *   timed refusal (`write_hold`, `not_ready`) is re-sent, on the hold
   *   budget; every other rejection throws immediately with
   *   `err.retryable` set. `retry: false` turns off every retry. Applies to
   *   both `sql()` and sockets from `connect()`.
   *   - `getToken` / `onUnauthorized`: the credential hooks for THIS client
   *   only. They win over `setAuthHooks()`. Omit both and the client uses the
   *   installed hooks, which is what an app wants.
   *   - `database` / `branch` (B1871): a default target. Every socket that
   *   `connect()` returns stamps them on each query (see
   *   `KineDBSocket.setContext()`); a database without a branch uses `main`.
   *   `sql()` sends the database as `POST /sql?db=<database>`. `POST /sql` has
   *   no branch selector, so `sql()` with a default branch other than `main`
   *   throws a KineDBError: use `connect()` or a qualified name
   *   (`db@branch.t`). A branch without a database throws a TypeError.
   */
  constructor(baseUrl, wsUrl, opts = {}) {
    this.baseUrl = baseUrl || defaultBaseUrl();
    this.wsUrl = wsUrl || defaultWsUrl();
    [this.database, this.branch] = defaultContext(opts?.database, opts?.branch);
    this._retry = normalizeRetryOpts(opts?.retry);
    // B495: the clock and the sleep of the retry loop. A test replaces both.
    this._now = Date.now;
    this._sleep = sleep;
    // Stored, not resolved: `setAuthHooks()` may run after this client is built,
    // and a client made at module load must still see the app's later install.
    this._auth = {
      getToken: typeof opts?.getToken === 'function' ? opts.getToken : null,
      onUnauthorized: typeof opts?.onUnauthorized === 'function' ? opts.onUnauthorized : null,
    };
  }

  /**
   * GET /health — deliberately BARE.
   *
   * `/health` is open by design on the server ("a liveness probe has no
   * credentials") and dispatches with no caller, so a bearer here would be pure
   * leakage: it is the one route a reverse proxy or uptime checker hits with no
   * relationship to any session.
   */
  async health() {
    const r = await fetch(joinUrl(this.baseUrl, '/health'));
    if (!r.ok) throw new KineDBError(await httpErrorMessage(r, '/health'));
    return unwrap(await r.json());
  }

  /**
   * POST /sql — one-shot query over plain HTTP.
   * For long-lived connections (WATCH, repeated queries), prefer `connect()`
   * which keeps a single WebSocket open. B2174: for a transaction use
   * `transaction()`; the server refuses BEGIN, COMMIT and ROLLBACK here.
   *
   * B05 slice 7: carries `Authorization: Bearer <tok>` whenever the auth store
   * holds a LIVE session. `getToken()` returns null past `expires_at_ms`, so an
   * expired tab stops presenting a dead token instead of presenting one — the
   * server counts a rejected bearer on `auth.bearer_rejected_total`, which the
   * gate pre-declares at zero, and the metric pollers here never stop.
   *
   * B05 slice 9: a 401 now clears the session and bounces the UI to the login
   * gate. This is the interceptor slice 7 could not write, and its comment said
   * so: `POST /sql` could not 401 at all, so an invalid bearer was served
   * UNAUTHENTICATED and the only expiry signal was the client-side
   * `expires_at_ms`. The server-side reasons a live token dies early are real and
   * client-side expiry cannot see any of them — sessions are node-local and
   * in-memory, so a node restart or a load balancer moving the tab to a different
   * node invalidates the token while the clock says it is fine.
   *
   * ONE injection point covers the whole app because this is the single HTTP
   * transport. The WebSocket path needs no equivalent: socket.js reconnects on a
   * 1s→30s backoff and re-authenticates from the stored credential each time.
   */
  async sql(sql, params) {
    // B2171: encode once, so a bad value is refused before the first POST.
    return this._sqlP(sql, encodeParams(params));
  }

  /**
   * B2173: call the function `name` with typed arguments.
   *
   * The client reads `SHOW FUNCTIONS` one time and keeps every signature.
   * Each argument is converted to the type of its parameter, and a value that
   * does not fit throws a TypeError or a RangeError before anything is sent.
   * The statement is `CALL name($1, …, $n)` with the typed parameters. A
   * stale signature (the server refusal `signature`) gives one new read and
   * one new send.
   *
   * @param {string} name the function name.
   * @param {Array|object} [args] the arguments in signature order, or an
   *   object by parameter name. A missing `NULL` parameter is null.
   * @returns the value of `RETURNS <type>` (a ROW is an object), or the rows
   *   of `RETURNS TABLE (…)` as objects.
   * @throws {KineDBError} for an unknown function, a function with no
   *   parameter types, or a server error.
   */
  call(name, args) {
    this._calls ??= new FunctionCalls({
      run: (sql, p) => this._sqlP(sql, p),
      makeError: (message, response) => new KineDBError(message, response),
    });
    return this._calls.call(name, args);
  }

  /** `sql()` with P already encoded (`null` for no params). */
  async _sqlP(sql, p) {
    const body = sqlParamsBodyOf(sql, p);
    return this._postRetried(() => this._postSql(sql, body), this._retry);
  }

  /**
   * B2174: ONE complete transaction in ONE `POST /sql`. The body is the
   * transaction payload (`encodeTransactionPayload`) under the content type
   * `application/vnd.kinedb.sql-txn`. The server commits all the statements or
   * none of them. No transaction stays open on the server.
   *
   * @param {Array<string|[string]|[string, Array]>} statements in order. The
   *   SDK puts consecutive statements with the same text and types in one
   *   group; the order never changes.
   * @param {{token?: string, retry?: boolean|object, db?: string, branch?: string}} [options]
   *   `token` is the commit token (B1472). `retry` has the shape of the
   *   constructor option and wins over it; a retry sends the WHOLE transaction
   *   again, never a part. `db` and `branch` win over the default of the client.
   * @returns the `created` response, or throws a KineDBError.
   */
  async transaction(statements, options = {}) {
    const branch = options.branch ?? this.branch;
    // The server refuses a transaction on a branch other than main (B605).
    // Refuse it here too, before the POST, as sql() does.
    refuseHttpBranch(branch);
    const body = encodeTransactionPayload(statements, {
      db: options.db ?? this.database,
      branch,
      token: options.token,
    });
    const retry = options.retry === undefined ? this._retry : normalizeRetryOpts(options.retry);
    return this._postRetried(() => this._postSql(null, body, SQL_TXN_CONTENT_TYPE), retry);
  }

  /** The retry loop of one `POST /sql` request. `post` sends one attempt. */
  async _postRetried(post, retry) {
    const startedMs = this._now();
    for (let attempt = 0; ;) {
      const resp = await post();
      // Server-flagged transient rejection: the request did NOT apply, and the
      // server says re-sending it is lossless. With the retry option on, wait
      // (`nextRetryDelay`: a write hold uses the time budget, every other
      // rejection uses maxRetries) and go again; otherwise fall through to
      // unwrap, whose KineDBError carries `.retryable` for caller-built loops.
      // A backoff response with no retry option keeps the old pass-through shape.
      const transient = resp?.type === 'backoff' || (resp?.type === 'error' && resp.retry === true);
      if (transient && retryAllows(retry, resp)) {
        const next = nextRetryDelay(resp, attempt, startedMs, this._now(), retry);
        if (!next) {
          if (resp.type === 'backoff') {
            throw new KineDBError(`server backoff: too many retries (${retry.maxRetries})`, resp);
          }
          return unwrap(resp);
        }
        await this._sleep(next.delayMs);
        attempt = next.attempt;
        continue;
      }
      return unwrap(resp);
    }
  }

  /**
   * One `POST /sql` attempt: bearer injection, 401 interception, JSON parse.
   * B2171: `body` is the params body (`encodeSqlParamsBody`), or `null` for
   * the plain-text body. B2174: `contentType` names the body.
   */
  async _postSql(sql, body = null, contentType = SQL_PARAMS_CONTENT_TYPE) {
    refuseHttpBranch(this.branch);
    const { getToken, onUnauthorized } = hooksFor(this._auth);
    const token = getToken ? getToken() : null;
    const path = this.database == null ? '/sql' : `/sql?db=${encodeURIComponent(this.database)}`;
    const r = await fetch(joinUrl(this.baseUrl, path), {
      method: 'POST',
      headers: {
        'content-type': body === null ? 'text/plain' : contentType,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === null ? sql : body,
    });
    // Before the throw, so a caller that swallows the error still gets logged out
    // rather than left holding a dead session and retrying forever.
    // B1338: name the refused credential (`null` for none). A 401 can arrive after
    // a new login, and only the sender knows it refused the OLD token.
    if (r.status === 401 && onUnauthorized) onUnauthorized(token);
    if (!r.ok) throw new KineDBError(await httpErrorMessage(r, '/sql'));
    return r.json();
  }

  /** Open a persistent WebSocket. Inherits this client's retry option. */
  connect() {
    const url = this.wsUrl;
    return new Promise((resolve, reject) => {
      let ws;
      try { ws = new WebSocket(url); }
      catch (e) { reject(e); return; }
      ws.binaryType = 'arraybuffer';
      ws.onopen  = () => resolve(new KineDBSocket(ws, {
        retry: this._retry, database: this.database, branch: this.branch,
      }));
      // B1852: a failed handshake is retryable. A server over its client
      // connection limit answers the upgrade with 503 and `Retry-After`, and
      // the WebSocket API does not show that status. A failed connect sent
      // nothing, so a retry is always safe.
      ws.onerror = () => {
        const err = new KineDBError(`WebSocket connection failed: ${url}`);
        err.retryable = true;
        reject(err);
      };
    });
  }
}

function isDev() {
  return typeof import.meta !== 'undefined' && !!import.meta.env?.DEV;
}

function basePathFromLocation() {
  if (typeof window === 'undefined') return '';
  const m = window.location.pathname.match(/^(.*?)\/ui(?:\/.*)?$/);
  return m ? m[1] : '';
}

export function defaultBaseUrl() {
  if (typeof window === 'undefined') return '';
  if (isDev()) return '';
  const { protocol, host } = window.location;
  return `${protocol}//${host}${basePathFromLocation()}`;
}

export function defaultWsUrl() {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_KINEDB_WS_URL) {
    return import.meta.env.VITE_KINEDB_WS_URL;
  }
  if (typeof window === 'undefined') return 'ws://127.0.0.1:4820/ws';
  if (isDev()) {
    return `ws://${window.location.hostname || '127.0.0.1'}:4820/ws`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${basePathFromLocation()}/ws`;
}

// ── Binary wire protocol ────────────────────────────────────────────

// Frame header: `[msg_type:1][flags:1][request_id:2 BE][payload_length:4 BE]`.
const FRAME_HEADER_SIZE = 8;
const FLAG_COMPRESSED   = 0x01;
const FLAG_HAS_ELAPSED  = 0x02;
// B607 — the typed result set. Both bits live in the frame `flags` byte, so
// neither costs a TLV field. Exported so the tests assert on the real bits.
//
//   FLAG_TYPED_ROWS   set on a `Query` (0x10) request of a connection with no
//                     login = "answer this request typed, with the header". It
//                     is a per-request grant, so a guest gets typed rows. A
//                     logged-in connection is typed by the login (B1514).
//   FLAG_WANT_SCHEMA  set on a `Query` (0x10) request = "send the column header
//                     back with the reply". Sent only on a schema-cache MISS.
//
// B1514 retired the typed opt-ins on `Authenticate` (0x04 and 0x08). The
// login carries CLIENT_PROTOCOL_VERSION instead, and a versioned login gets
// the typed shapes only: `TypedRows`, `TypedRowChunk` and `WatchTypedData`.
export const FLAG_TYPED_ROWS  = 0x04;
export const FLAG_WANT_SCHEMA = 0x08;
// B1514: the wire version that this client speaks. It rides slot 4 of every
// `Authenticate`. A server refuses a login with no version or a version
// below its minimum, and its error tells the user to upgrade the SDK.
// B2200: version 2 is the paced fill. The client asks for each next page of a
// fill with `WatchFillNext` (0x29). A server of version 2 refuses a login of 1.
export const CLIENT_PROTOCOL_VERSION = 2;
// B971, the files family (0x30-0x37).
//
//   FLAG_FILES  set on the client's `Authenticate` (0x18) = "I speak the file
//               messages". The server ECHOES it on the `AuthResult` (0x19)
//               frame when it grants it. No echo means a server with no file
//               support, and `putFile` / `getFile` refuse rather than hang: an
//               old server DROPS an unknown frame in silence and never replies.
export const FLAG_FILES = 0x40;
// B984, the skip hint (0x38-0x39).
//
//   FLAG_FILE_SKIP  set on the client's `Authenticate` (0x18) = "I ask which
//                   chunks the shard holds". The server ECHOES it on the
//                   `AuthResult` (0x19) frame when it grants it. No echo means
//                   no hint: `putFile` sends every chunk, because an old server
//                   drops a 0x38 frame in silence. It is independent of
//                   FLAG_FILES.
export const FLAG_FILE_SKIP = 0x80;

// Maximum number of times `KineDBSocket._onFrame` will auto-retry a query
// in response to `Response::Backoff` before giving up with a `KineDBError`.
// Matches the 50-cap on the kd-bench protocol client (Phase 2). 50 retries
// at the server's typical 25-100ms hint = ~1.2-5s worst-case before the
// caller's promise rejects. Browser callers are interactive, so we don't
// bound by wall-clock deadline the way bench does.
const WS_BACKOFF_MAX_RETRIES = 50;

// B05 slice 7: receive budget for the WS login. Generous because the server runs
// 600k iterations of BLAKE3-PBKDF on the connection's own main-loop thread
// (~50-100ms idle, more on a busy node), bounded because a server predating
// `0x18` never replies at all and the browser would otherwise hang forever.
const WS_LOGIN_TIMEOUT_MS = 10_000;

// TLV tags (mirror crates/kinedb-protocol/src/tlv.rs).
const T_NULL       = 0x00;
const T_BOOL_FALSE = 0x01;
const T_BOOL_TRUE  = 0x02;
const T_INT64      = 0x03;
const T_FLOAT64    = 0x04;
const T_STRING     = 0x05;
const T_BYTES      = 0x06;
const T_TIMESTAMP  = 0x07;
const T_INT_VAR    = 0x08;
const T_ARRAY      = 0x10;
const T_MAP        = 0x11;

// Message-type discriminants (mirror crates/kinedb-protocol/src/messages.rs).
export const MT_QUERY = 0x10;
const MT_ROWS         = 0x11;
const MT_ERROR        = 0x12;
const MT_CREATED      = 0x13;
const MT_INSERTED     = 0x14;
const MT_UPDATED      = 0x15;
const MT_DELETED      = 0x16;
const MT_BACKOFF      = 0x17;
// B05 slice 7: the client login pair. Exported so the test can assert on the
// real discriminants rather than re-spelling them.
export const MT_AUTHENTICATE = 0x18;
export const MT_AUTH_RESULT  = 0x19;
// B2174: one complete transaction in one frame. The reply is `Created` or
// `Error`, as for a write Query.
export const MT_TRANSACTION = 0x1E;
// B607: the typed result set. `TypedRows` replaces `Rows` (0x11) and
// `TypedRowChunk` replaces `RowChunk` (0x9C) on a request that sets
// FLAG_TYPED_ROWS. The terminal frame of a stream stays `RowStreamEnd` (0x9D).
export const MT_TYPED_ROWS      = 0x1A;
export const MT_TYPED_ROW_CHUNK = 0x1B;
export const MT_ROW_STREAM_END = 0x9D;
// B1644: the legacy chunk of a plain Query reply (no typed grant).
export const MT_ROW_CHUNK = 0x9C;
const MT_WATCHING     = 0x20;
const MT_UNWATCHED    = 0x21;
const MT_WATCH_NOTIFY = 0x22;
// B1514: the legacy VERBOSE push `WatchData` (0x23) is retired. It carried the
// lossy JSON cells. A server never sends it to a versioned login, so a 0x23
// frame is a protocol error: the client fails the connection.
const MT_WATCH_DATA_RETIRED = 0x23;
// B1168: the typed VERBOSE push. The client hands it to a callback as
// `watch_data`. Since B1514 it is the only VERBOSE push.
export const MT_WATCH_TYPED_DATA = 0x27;
// B1156: the two other push frames. `WatchFill` is the resume fill of a
// `WATCH … SINCE` subscription (B1147); `WatchTail` is one diff of the live
// tail of a `WATCH … SINCE … LIVE` subscription (B1529). Each carries request
// id 0 and answers no request. The client hands them to a callback as
// `watch_fill` and `watch_tail`.
export const MT_WATCH_FILL = 0x24;
export const MT_WATCH_TAIL = 0x28;
// B2200: the request of the paced fill, `Array[String table]`. A 0x24 or 0x28
// frame with `more == true` waits: the server sends the next page of that
// table only after it reads this request. It has no reply. With no fill of
// the table in progress, the server answers ONE `Error` with its request id.
export const MT_WATCH_FILL_NEXT = 0x29;
// B971: the files family. A file of any size rides the wire as content-defined
// chunks. The client sends one `PutFileChunk` per chunk, then one `PutFile`
// with the manifest. It reads a file back with `GetFile` plus one
// `GetFileChunk` per hash. B984 took 0x38 and 0x39 for the skip hint; 0x3A is
// the tripwire now. All ten are exported so a test asserts on the real
// discriminants.
export const MT_PUT_FILE_CHUNK = 0x30;
export const MT_FILE_CHUNK_PUT = 0x31;
export const MT_PUT_FILE       = 0x32;
export const MT_FILE_PUT       = 0x33;
export const MT_GET_FILE       = 0x34;
export const MT_FILE_MANIFEST  = 0x35;
export const MT_GET_FILE_CHUNK = 0x36;
export const MT_FILE_CHUNK     = 0x37;
export const MT_FIND_HELD_CHUNKS = 0x38;
export const MT_HELD_CHUNKS      = 0x39;
const MT_HEALTH_REPLY = 0x41;

const FILE_MSG_NAMES = Object.freeze({
  0x30: 'PutFileChunk', 0x31: 'FileChunkPut', 0x32: 'PutFile', 0x33: 'FilePut',
  0x34: 'GetFile', 0x35: 'FileManifest', 0x36: 'GetFileChunk', 0x37: 'FileChunk',
  0x38: 'FindHeldChunks', 0x39: 'HeldChunks',
});

/**
 * B984: the most hashes one `FindHeldChunks` may name. A client splits a
 * longer list. It is a multiple of 8, so the bitmaps of consecutive requests
 * join into one bitmap with no gap.
 */
export const FIND_HELD_CHUNKS_MAX = 16384;

/** B984: the byte length of a `HeldChunks` bitmap for `n` hashes. */
export function heldBitmapLen(n) {
  return Math.ceil(n / 8);
}

/**
 * B984: whether bit `i` is set. Bit `i` is bit `i % 8` of byte `i / 8`, least
 * significant bit first. A bit past the end reads as not held.
 */
export function heldBitmapGet(held, i) {
  const byte = Math.floor(i / 8);
  if (byte >= held.length) return false;
  return (held[byte] & (1 << (i % 8))) !== 0;
}

/** B984: set bit `i` of a `Uint8Array` bitmap. A bit past the end is ignored. */
export function heldBitmapSet(held, i) {
  const byte = Math.floor(i / 8);
  if (byte < held.length) held[byte] |= 1 << (i % 8);
}

/** `PutFileChunk (0x30)`, for an error message that names the opcode. */
export function fileMsgName(msgType) {
  const name = FILE_MSG_NAMES[msgType] ?? 'unknown';
  return `${name} (0x${msgType.toString(16).padStart(2, '0')})`;
}

// B971: the receive budget of ONE file frame, not of a whole file. A file has
// no size limit, so a budget for the whole transfer would have no honest
// value. Each chunk is at most 128 KiB, so 60 seconds is generous for one
// frame and still bounds a server that never answers.
export const FILE_REQUEST_TIMEOUT_MS = 60_000;

// B971: how many chunk frames may be unanswered at once. It mirrors
// `FILE_UPLOAD_WINDOW` in `crates/kinedb-protocol/src/client.rs`.
export const FILE_UPLOAD_WINDOW = 4;

// B607 column type codes. The type lives ONCE in the reply header; a CELL
// carries no tag at all, so these codes are what decides the byte layout of
// every cell in a column. Code 0 means "every cell in this column is NULL" —
// an all-NULL column, or a column of a result with no rows — and such a column
// never has a cell on the wire.
export const COLUMN_CODES = Object.freeze({
  Null: 0, Bool: 1, Int: 2, Float: 3, Text: 4,
  Bytes: 5, Timestamp: 6, Json: 7, Blob: 8, Vector: 9,
});
const COLUMN_CODE_NAMES = Object.freeze([
  'Null', 'Bool', 'Int', 'Float', 'Text', 'Bytes', 'Timestamp', 'Json', 'Blob', 'Vector',
]);

/** Name of a column type code, for `result.types[i].name` and the UI. */
export function columnTypeName(code) {
  return COLUMN_CODE_NAMES[code] ?? `Unknown(${code})`;
}

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder('utf-8');

// A JS Number holds an integer exactly only inside this window. Past it the
// typed decoder hands back a BigInt rather than a rounded Number — losing the
// low bits of an INT is exactly the bug B607 exists to fix.
const MAX_SAFE_BIG = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE_BIG = -MAX_SAFE_BIG;

function readU32BE(buf, pos) {
  return ((buf[pos] << 24) | (buf[pos+1] << 16) | (buf[pos+2] << 8) | buf[pos+3]) >>> 0;
}

// LZ4 block decompress matching kinedb-compress format: 4-byte LE uncompressed
// length prefix followed by a standard LZ4 block. The server's `encode_frame`
// auto-compresses any payload > 256 bytes whose compressed form is strictly
// smaller — independent of the WS-level `kinedb-lz4` subprotocol — so the
// browser client MUST be able to decompress responses even when it never
// negotiated LZ4 at the WS layer.
function lz4Decompress(src) {
  if (src.length < 4) throw new Error('lz4: truncated header');
  const ulen = (src[0] | (src[1] << 8) | (src[2] << 16) | (src[3] << 24)) >>> 0;
  if (ulen === 0) return new Uint8Array(0);
  const dst = new Uint8Array(ulen);
  let sp = 4, dp = 0;
  const srcLen = src.length;
  while (sp < srcLen) {
    const token = src[sp++];
    let litLen = token >> 4;
    if (litLen === 15) {
      let b;
      do { b = src[sp++]; litLen += b; } while (b === 255);
    }
    if (sp + litLen > srcLen || dp + litLen > ulen) {
      throw new Error('lz4: literal overrun');
    }
    for (let i = 0; i < litLen; i++) dst[dp++] = src[sp++];
    // End-of-sequence: last literal block, no match follows.
    if (sp >= srcLen) break;
    if (sp + 2 > srcLen) throw new Error('lz4: offset truncated');
    const offset = src[sp] | (src[sp+1] << 8);
    sp += 2;
    let matchLen = token & 0x0F;
    if (matchLen === 15) {
      let b;
      do { b = src[sp++]; matchLen += b; } while (b === 255);
    }
    matchLen += 4;
    const matchStart = dp - offset;
    if (matchStart < 0 || dp + matchLen > ulen) {
      throw new Error('lz4: match overrun');
    }
    // Byte-by-byte copy: overlapping matches (offset < matchLen) are part of
    // the LZ4 spec and require sequential semantics, not bulk copy.
    for (let i = 0; i < matchLen; i++) dst[dp++] = dst[matchStart + i];
  }
  if (dp !== ulen) throw new Error(`lz4: expected ${ulen} bytes, got ${dp}`);
  return dst;
}

// TLV decode. Returns the JS value and the next position. Numbers are returned
// as JS Number; values > 2^53-1 will lose precision (none of the server's
// response variants put unbounded counters here today). Bytes are returned as
// Uint8Array.
//
// `opts.exactInt64` keeps a T_INT64 as a BigInt instead of narrowing it to a
// Number. Only the typed row messages set it: their `schema_id` is a CONTENT
// HASH, so all 64 bits are random and `Number` would round most of them away —
// two different schemas would then compare equal and the client would decode a
// changed result set with a stale header. Every other message keeps the old
// Number behaviour, so no existing caller sees a type change.
export function decodeTlv(buf, pos, opts) {
  const tag = buf[pos++];
  switch (tag) {
    case T_NULL: return [null, pos];
    case T_BOOL_FALSE: return [false, pos];
    case T_BOOL_TRUE:  return [true,  pos];
    case T_INT64: {
      const dv = new DataView(buf.buffer, buf.byteOffset + pos, 8);
      const v = dv.getBigInt64(0, false);
      return [opts?.exactInt64 ? v : Number(v), pos + 8];
    }
    case T_FLOAT64: {
      const dv = new DataView(buf.buffer, buf.byteOffset + pos, 8);
      return [dv.getFloat64(0, false), pos + 8];
    }
    case T_STRING: {
      const len = readU32BE(buf, pos); pos += 4;
      const s = TEXT_DECODER.decode(buf.subarray(pos, pos + len));
      return [s, pos + len];
    }
    case T_BYTES: {
      const len = readU32BE(buf, pos); pos += 4;
      return [buf.subarray(pos, pos + len), pos + len];
    }
    case T_TIMESTAMP: {
      const dv = new DataView(buf.buffer, buf.byteOffset + pos, 8);
      return [Number(dv.getBigInt64(0, false)), pos + 8];
    }
    case T_INT_VAR: {
      // LEB128 → zigzag-decode i64. Use BigInt for the accumulator: shifts
      // past 25 bits otherwise overflow JS's 32-bit bitwise lane and silently
      // truncate values like microsecond timestamps or large row counts.
      let raw = 0n;
      let shift = 0n;
      for (let i = 0; i < 10; i++) {
        const b = buf[pos++];
        raw |= BigInt(b & 0x7F) << shift;
        if ((b & 0x80) === 0) {
          const signed = (raw >> 1n) ^ -(raw & 1n);
          return [Number(signed), pos];
        }
        shift += 7n;
      }
      throw new Error('tlv: varint overflow');
    }
    case T_ARRAY: {
      const count = readU32BE(buf, pos); pos += 4;
      const arr = new Array(count);
      for (let i = 0; i < count; i++) {
        const [v, next] = decodeTlv(buf, pos, opts);
        arr[i] = v; pos = next;
      }
      return [arr, pos];
    }
    case T_MAP: {
      const count = readU32BE(buf, pos); pos += 4;
      const obj = {};
      for (let i = 0; i < count; i++) {
        const [k, p1] = decodeTlv(buf, pos, opts);
        const [v, p2] = decodeTlv(buf, p1, opts);
        obj[String(k)] = v;
        pos = p2;
      }
      return [obj, pos];
    }
    default:
      throw new Error(`tlv: unknown tag 0x${tag.toString(16).padStart(2, '0')}`);
  }
}

// Build a TLV-encoded Query payload: positional Array of [String(sql), Bool(forwarded)].
//
// B1871: with a default database the Array has 4 slots:
// [sql, false, String(db), String(branch)]. An absent half is the empty
// string, the server's sentinel for "the session's". The socket always sends
// both halves (a database without a branch sends `main`).
//
// B2171: with params the Array has 7 slots:
// [sql, false, String(db), String(branch), Null, Null, P]. Slot 5 (the forward
// deadline) and slot 6 (the metadata floor) are peer slots, so a client writes
// their `Null` sentinel. P is `encodeParams(params)`. No params (`undefined`,
// `null` or `[]`) keeps the old bytes exactly.
export function encodeQueryPayload(sql, db = null, branch = null, params = null) {
  return queryPayload(sql, db, branch, encodeParams(params));
}

/** `encodeQueryPayload` with P already encoded (`null` for no params). */
function queryPayload(sql, db, branch, params) {
  if (params !== null) {
    return tlvArray([
      tlvString(sql),
      Uint8Array.of(T_BOOL_FALSE),
      tlvString(db ?? ''),
      tlvString(branch ?? ''),
      Uint8Array.of(T_NULL),
      Uint8Array.of(T_NULL),
      params,
    ]);
  }
  if (db != null || branch != null) {
    return tlvArray([
      tlvString(sql),
      new Uint8Array([T_BOOL_FALSE]),
      tlvString(db ?? ''),
      tlvString(branch ?? ''),
    ]);
  }
  const sqlBytes = TEXT_ENCODER.encode(sql);
  // [tag ARRAY][u32 count=2][tag STRING][u32 len][sql bytes][tag BOOL_FALSE]
  const out = new Uint8Array(1 + 4 + 1 + 4 + sqlBytes.length + 1);
  let p = 0;
  out[p++] = T_ARRAY;
  out[p++] = 0; out[p++] = 0; out[p++] = 0; out[p++] = 2;
  out[p++] = T_STRING;
  out[p++] = (sqlBytes.length >>> 24) & 0xFF;
  out[p++] = (sqlBytes.length >>> 16) & 0xFF;
  out[p++] = (sqlBytes.length >>> 8) & 0xFF;
  out[p++] = sqlBytes.length & 0xFF;
  out.set(sqlBytes, p); p += sqlBytes.length;
  out[p++] = T_BOOL_FALSE; // `forwarded` — always false from the browser
  return out;
}

// B05 slice 7: TLV-encode `Request::Authenticate` — a positional `Array` of
// exactly the shape `kinedb-protocol/src/message.rs` emits.
//
// Lengths are BYTE lengths of the UTF-8 encoding, not character counts: a
// non-ASCII password (`pä€ss`) would otherwise under-declare its length and the
// server would see a truncated frame — an indistinguishable 401 for a password
// the user typed correctly.
//
// B1514: the frame always has 4 slots: `[String username, String password,
// String token | Null, IntVar protocol_version]`. With a token the server
// validates the token instead of the password. The version is
// CLIENT_PROTOCOL_VERSION; a login without it is refused.
export function encodeAuthenticatePayload(username, password, token) {
  return tlvArray([
    tlvString(username),
    tlvString(password),
    tlvOptionString(token),
    tlvIntVar(CLIENT_PROTOCOL_VERSION),
  ]);
}

// B2200: the payload of `WatchFillNext` (0x29), `Array[String table]`.
export function encodeWatchFillNextPayload(table) {
  return tlvArray([tlvString(table)]);
}

// ── B971: the four file requests ────────────────────────────────────────────
//
// Every file payload is a positional ARRAY, in the order
// `sdk/test-vectors/files-wire-v1.json` declares. `db` and `branch` are
// `Option<String>`, so Null when absent. A hash is Bytes(32). A chunk list is
// an ARRAY of Bytes(32). An index or a size is IntVar.

function tlvConcat(parts) {
  let total = 0;
  for (const part of parts) total += part.length;
  const out = new Uint8Array(total);
  let p = 0;
  for (const part of parts) { out.set(part, p); p += part.length; }
  return out;
}

function u32be(n) {
  return Uint8Array.of((n >>> 24) & 0xFF, (n >>> 16) & 0xFF, (n >>> 8) & 0xFF, n & 0xFF);
}

function tlvString(text) {
  const bytes = TEXT_ENCODER.encode(String(text));
  return tlvConcat([Uint8Array.of(T_STRING), u32be(bytes.length), bytes]);
}

function tlvBytes(data) {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data || 0);
  return tlvConcat([Uint8Array.of(T_BYTES), u32be(bytes.length), bytes]);
}

// A BigInt accumulator, because a size past 2^31 overflows JS's 32-bit
// bitwise lane and would silently truncate the field.
function tlvIntVar(value) {
  const signed = BigInt(value);
  let raw = (signed << 1n) ^ (signed >> 63n);
  if (raw < 0n) throw new KineDBError(`tlv: an IntVar below -2^63: ${value}`);
  const out = [T_INT_VAR];
  for (;;) {
    const byte = Number(raw & 0x7Fn);
    raw >>= 7n;
    if (raw > 0n) { out.push(byte | 0x80); } else { out.push(byte); break; }
  }
  return Uint8Array.from(out);
}

function tlvArray(items) {
  return tlvConcat([Uint8Array.of(T_ARRAY), u32be(items.length), ...items]);
}

/** An `Option<String>` field: Null when absent, a String when present. */
function tlvOptionString(text) {
  return text == null ? Uint8Array.of(T_NULL) : tlvString(text);
}

/** A 32-byte hash as lower-case hex. The one shape the API hands out. */
function hexOf(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || 0);
  let out = '';
  for (let i = 0; i < u8.length; i += 1) out += u8[i].toString(16).padStart(2, '0');
  return out;
}

/** The reverse: 64 hex characters back to the 32 bytes the wire carries. */
function fromHex32(text) {
  if (typeof text !== 'string' || text.length !== 64) {
    throw new KineDBError(`a blake3 hash is 64 hex characters, got ${text?.length}`);
  }
  const out = new Uint8Array(32);
  for (let i = 0; i < 32; i += 1) out[i] = parseInt(text.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** Check one 32-byte hash. A short hash would name a wrong file. */
function hash32(value, what) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value || 0);
  if (bytes.length !== 32) {
    throw new KineDBError(`${what}: a blake3 hash is 32 bytes, got ${bytes.length}`);
  }
  return bytes;
}

/** `PutFileChunk` (0x30): one content-defined chunk of a file. */
export function encodePutFileChunkPayload(db, branch, collection, name, index, hash, data) {
  return tlvArray([
    tlvOptionString(db),
    tlvOptionString(branch),
    tlvString(collection),
    tlvString(name),
    tlvIntVar(index),
    tlvBytes(hash32(hash, 'putFile chunk')),
    tlvBytes(data),
  ]);
}

/** `PutFile` (0x32): the manifest that closes an upload. */
export function encodePutFilePayload(db, branch, collection, name, size, hash, chunkHashes) {
  return tlvArray([
    tlvOptionString(db),
    tlvOptionString(branch),
    tlvString(collection),
    tlvString(name),
    tlvIntVar(size),
    tlvBytes(hash32(hash, 'putFile')),
    tlvArray(Array.from(chunkHashes, (h) => tlvBytes(hash32(h, 'putFile chunk')))),
  ]);
}

/** `GetFile` (0x34): ask for the manifest of one file. */
export function encodeGetFilePayload(db, branch, collection, name) {
  return tlvArray([
    tlvOptionString(db),
    tlvOptionString(branch),
    tlvString(collection),
    tlvString(name),
  ]);
}

/** `GetFileChunk` (0x36): ask for the bytes of one chunk. */
export function encodeGetFileChunkPayload(db, branch, collection, name, hash) {
  return tlvArray([
    tlvOptionString(db),
    tlvOptionString(branch),
    tlvString(collection),
    tlvString(name),
    tlvBytes(hash32(hash, 'getFileChunk')),
  ]);
}

/**
 * B984: the chunk indexes the first pass of `putFile` sends.
 *
 * `hashes` is the hex hash of each chunk, in file order. `held` answers the
 * DISTINCT hashes in the order of their first occurrence. The plan is the
 * first index of each distinct hash that is not held, in file order. A
 * repeated hash is sent once: one upload covers every index that names it.
 */
function planChunkSends(hashes, held) {
  const seen = new Set();
  const plan = [];
  hashes.forEach((h, i) => {
    if (seen.has(h)) return;
    seen.add(h);
    if (!heldBitmapGet(held, seen.size - 1)) plan.push(i);
  });
  return plan;
}

/** B984 `FindHeldChunks` (0x38): ask which chunk hashes the shard holds. */
export function encodeFindHeldChunksPayload(db, branch, collection, name, hashes) {
  const list = Array.from(hashes);
  if (list.length > FIND_HELD_CHUNKS_MAX) {
    throw new KineDBError(
      `findHeldChunks: ${list.length} hashes is above FIND_HELD_CHUNKS_MAX `
      + `(${FIND_HELD_CHUNKS_MAX}) per request`);
  }
  return tlvArray([
    tlvOptionString(db),
    tlvOptionString(branch),
    tlvString(collection),
    tlvString(name),
    tlvArray(list.map((h) => tlvBytes(hash32(h, 'findHeldChunks')))),
  ]);
}

// Wrap a TLV payload in a frame header. We never set FLAG_COMPRESSED on
// outgoing requests; the server's `decode_request` accepts uncompressed
// frames unconditionally, so the browser doesn't need an LZ4 encoder.
//
// `flags` carries the B607 capability bits: FLAG_TYPED_ROWS on `Authenticate`
// and FLAG_WANT_SCHEMA on `Query`, plus B971's FLAG_FILES. An old server
// ignores every one of them.
export function buildFrame(msgType, requestId, payload, flags = 0) {
  const out = new Uint8Array(FRAME_HEADER_SIZE + payload.length);
  out[0] = msgType;
  out[1] = flags & 0xFF;
  out[2] = (requestId >>> 8) & 0xFF;
  out[3] = requestId & 0xFF;
  out[4] = (payload.length >>> 24) & 0xFF;
  out[5] = (payload.length >>> 16) & 0xFF;
  out[6] = (payload.length >>> 8) & 0xFF;
  out[7] = payload.length & 0xFF;
  out.set(payload, FRAME_HEADER_SIZE);
  return out;
}

// Parse a complete frame. Decompresses if FLAG_COMPRESSED is set and strips
// the leading 8-byte elapsed_us prefix when FLAG_HAS_ELAPSED is set.
export function parseFrame(data) {
  const u8 = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
  if (u8.length < FRAME_HEADER_SIZE) throw new Error('frame: header truncated');
  const msgType = u8[0];
  const flags = u8[1];
  const requestId = (u8[2] << 8) | u8[3];
  const payloadLen = readU32BE(u8, 4);
  if (u8.length < FRAME_HEADER_SIZE + payloadLen) {
    throw new Error(`frame: payload truncated (have ${u8.length - FRAME_HEADER_SIZE}, want ${payloadLen})`);
  }
  let payload = u8.subarray(FRAME_HEADER_SIZE, FRAME_HEADER_SIZE + payloadLen);
  if (flags & FLAG_COMPRESSED) {
    payload = lz4Decompress(payload);
  }
  let elapsedUs = null;
  if (flags & FLAG_HAS_ELAPSED) {
    if (payload.length < 8) throw new Error('frame: elapsed prefix truncated');
    const dv = new DataView(payload.buffer, payload.byteOffset, 8);
    elapsedUs = Number(dv.getBigUint64(0, false));
    payload = payload.subarray(8);
  }
  return { msgType, requestId, flags, payload, elapsedUs };
}

// Each Rows / WatchData cell is wired as TLV `Bytes` holding the JSON
// encoding of one `serde_json::Value`. Re-parse it back to the native JS
// value so callers see the same `r.data[i][j]` shape they did under JSON.
function decodeJsonCell(cellBytes) {
  if (!cellBytes || cellBytes.length === 0) return null;
  const s = TEXT_DECODER.decode(cellBytes);
  try { return JSON.parse(s); } catch { return s; }
}

// ── B607: the typed row blob ────────────────────────────────────────────────

/**
 * Decode the ONE `Bytes` blob a `TypedRows` / `TypedRowChunk` reply carries.
 *
 * Layout, row-major, exactly as `typed_rows::encode_blob` writes it:
 *
 *   per row: a NULL bitmap of `ceil(ncols/8)` bytes — bit `i` (LSB-first, byte
 *   `i/8`, bit `i%8`) set ⇔ column `i` is NULL — then the NON-NULL cells in
 *   column order, each with NO tag. The column's type fixes the cell layout:
 *
 *     Bool       1 byte, 0 or 1
 *     Int        zigzag unsigned-LEB128 varint
 *     Timestamp  zigzag unsigned-LEB128 varint, microseconds
 *     Float      8 bytes, f64 little-endian
 *     Text       unsigned-LEB128 BYTE length + UTF-8 bytes
 *     Bytes      unsigned-LEB128 length + raw bytes
 *     Json       unsigned-LEB128 length + the raw JSON bytes
 *     Blob       unsigned-LEB128 count + that many 32-byte content hashes
 *     Vector     `param` × f32 little-endian, NO length prefix
 *
 * A NULL costs one bit and zero bytes, so a row of N NULLs is `ceil(N/8)`
 * bytes and nothing else. A code-0 column never has a cell.
 *
 * @param {Array<{code:number, param?:number}>} columns  the column header — the
 *   reply's own, or the one this client cached for that `schemaId`.
 * @param {Uint8Array} bytes  the blob.
 * @param {number} [expectedCount]  the reply's row count. Checked when given:
 *   a blob that decodes to a different number of rows means the client and the
 *   server disagree about the layout, which must be loud, not silent.
 * @returns {Array<Array<*>>} rows of decoded cells.
 * @throws {Error} on a truncated blob or an unknown column code. NEVER returns
 *   a partly decoded row: a wrong cell is worse than a visible failure.
 */
export function decodeTypedBlob(columns, bytes, expectedCount) {
  const cols = columns || [];
  const ncols = cols.length;
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || 0);
  // `count` rides the wire as `int_optimal`, which is an Int64 past 2^48, and a
  // typed payload decodes Int64 exactly — so it can be a BigInt. Normalise
  // before comparing: `3 !== 3n` is true and would fail a correct blob.
  const want = expectedCount == null ? null : Number(expectedCount);
  // Zero columns means zero cells per row, so the row count is unrecoverable
  // from the bytes: an EMPTY blob is the only decodable form, exactly as
  // `typed_rows::decode_blob` rules on the Rust side.
  if (ncols === 0) {
    if (u8.length > 0) {
      throw new Error(`typed rows: ${u8.length} blob bytes under a header with no columns`);
    }
    return [];
  }

  const bitmapLen = (ncols + 7) >> 3;
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const rows = [];
  let p = 0;

  const need = (n, what) => {
    if (p + n > u8.length) {
      throw new Error(
        `typed rows: truncated ${what} at byte ${p} (need ${n}, have ${u8.length - p})`);
    }
  };

  /** Unsigned LEB128 — a length or a count, never negative. */
  const readULeb = (what) => {
    let result = 0, shift = 0;
    for (let i = 0; i < 10; i++) {
      if (p >= u8.length) throw new Error(`typed rows: truncated ${what} varint at byte ${p}`);
      const b = u8[p++];
      result += (b & 0x7F) * 2 ** shift;
      if ((b & 0x80) === 0) return result;
      shift += 7;
    }
    throw new Error(`typed rows: ${what} varint overflow`);
  };

  /** Zigzag LEB128 → BigInt. BigInt accumulator on purpose: a shift past 25
   *  bits overflows JS's 32-bit bitwise lane and silently truncates. */
  const readZigZag = (what) => {
    let raw = 0n, shift = 0n;
    for (let i = 0; i < 10; i++) {
      if (p >= u8.length) throw new Error(`typed rows: truncated ${what} varint at byte ${p}`);
      const b = u8[p++];
      raw |= BigInt(b & 0x7F) << shift;
      if ((b & 0x80) === 0) return (raw >> 1n) ^ -(raw & 1n);
      shift += 7n;
    }
    throw new Error(`typed rows: ${what} varint overflow`);
  };

  /** A Number while that is exact; a BigInt the moment it stops being. */
  const narrow = (big) => (big <= MAX_SAFE_BIG && big >= MIN_SAFE_BIG) ? Number(big) : big;

  while (p < u8.length) {
    need(bitmapLen, 'NULL bitmap');
    const bmp = p;
    p += bitmapLen;
    const row = new Array(ncols);
    for (let i = 0; i < ncols; i++) {
      const code = cols[i]?.code | 0;
      const isNull = (u8[bmp + (i >> 3)] >> (i & 7)) & 1;
      // A code-0 column is NULL by definition and carries no cell, whatever
      // its bit says — reading one would desynchronise the whole rest of the row.
      if (isNull || code === COLUMN_CODES.Null) { row[i] = null; continue; }
      switch (code) {
        case COLUMN_CODES.Bool:
          need(1, `Bool cell (column ${i})`);
          row[i] = u8[p++] !== 0;
          break;
        case COLUMN_CODES.Int:
          row[i] = narrow(readZigZag(`Int cell (column ${i})`));
          break;
        case COLUMN_CODES.Timestamp:
          row[i] = narrow(readZigZag(`Timestamp cell (column ${i})`));
          break;
        case COLUMN_CODES.Float:
          need(8, `Float cell (column ${i})`);
          row[i] = dv.getFloat64(p, true);
          p += 8;
          break;
        case COLUMN_CODES.Text: {
          const len = readULeb(`Text length (column ${i})`);
          need(len, `Text cell (column ${i})`);
          row[i] = TEXT_DECODER.decode(u8.subarray(p, p + len));
          p += len;
          break;
        }
        case COLUMN_CODES.Bytes: {
          const len = readULeb(`Bytes length (column ${i})`);
          need(len, `Bytes cell (column ${i})`);
          // `slice`, not `subarray`: a subarray keeps the WHOLE frame buffer
          // alive for as long as any one cell is referenced.
          row[i] = u8.slice(p, p + len);
          p += len;
          break;
        }
        case COLUMN_CODES.Json: {
          const len = readULeb(`Json length (column ${i})`);
          need(len, `Json cell (column ${i})`);
          const text = TEXT_DECODER.decode(u8.subarray(p, p + len));
          p += len;
          // Same fallback as the untyped path: show the text rather than fail
          // the whole result set over one unparseable cell.
          try { row[i] = JSON.parse(text); } catch { row[i] = text; }
          break;
        }
        case COLUMN_CODES.Blob: {
          const n = readULeb(`Blob chunk count (column ${i})`);
          need(n * 32, `Blob hashes (column ${i})`);
          const hashes = new Array(n);
          for (let k = 0; k < n; k++) { hashes[k] = u8.slice(p, p + 32); p += 32; }
          row[i] = hashes;
          break;
        }
        case COLUMN_CODES.Vector: {
          const dim = cols[i]?.param | 0;
          need(dim * 4, `Vector cell (column ${i})`);
          const vec = new Float32Array(dim);
          for (let k = 0; k < dim; k++) vec[k] = dv.getFloat32(p + k * 4, true);
          p += dim * 4;
          row[i] = vec;
          break;
        }
        default:
          throw new Error(`typed rows: unknown column type code ${code} (column ${i})`);
      }
    }
    rows.push(row);
  }

  if (want != null && rows.length !== want) {
    throw new Error(`typed rows: blob decoded ${rows.length} rows, header says ${want}`);
  }
  return rows;
}

/**
 * `schema_id` as a 16-digit lowercase hex string.
 *
 * The wire carries the u64 content hash cast `as i64` in a TLV Int64, so half
 * the ids arrive negative. Hex normalises sign AND type, which is the point:
 * the id is only ever compared, and one canonical string makes `===` correct
 * and makes it usable as a Map key.
 */
function toSchemaId(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  const b = typeof v === 'bigint' ? v : BigInt(Math.trunc(v));
  return BigInt.asUintN(64, b).toString(16).padStart(16, '0');
}

/** Wire column triples `[name, code, param]` → `{columns:[name], types:[{code,name,param}]}`. */
function unpackColumns(wireCols) {
  const types = wireCols.map(c => ({
    code: c[1] | 0,
    name: columnTypeName(c[1] | 0),
    param: c[2] | 0,
  }));
  return { columns: wireCols.map(c => c[0]), types };
}

/**
 * Fill in `resp` from a typed payload's `[schema_id, columns, rows]` prefix.
 *
 * The schema is on the wire ONLY when the request asked for it, so there are
 * three cases and the third is the interesting one:
 *
 *  1. the reply carries columns          → decode, and the caller caches them;
 *  2. no columns, `schemaId` matches the cached entry → decode with the cache;
 *  3. no columns and no MATCHING entry   → `needsSchema`. Never decode: the
 *     cached header belongs to a different schema, and using it would produce
 *     confidently wrong cells rather than an error. This is what makes an
 *     `ALTER TABLE` between two runs of the same SQL safe.
 */
function fillTypedRows(resp, schemaIdRaw, wireCols, blob, ctx, count) {
  const schemaId = toSchemaId(schemaIdRaw);
  resp.schemaId = schemaId;

  const blobLen = blob ? blob.length : 0;
  let header = null;
  if (Array.isArray(wireCols) && wireCols.length > 0) {
    header = unpackColumns(wireCols);
  } else if (ctx?.schemaEntry && ctx.schemaEntry.schemaId === schemaId) {
    header = { columns: ctx.schemaEntry.columns, types: ctx.schemaEntry.types };
  } else if (ctx?.wantSchema && blobLen === 0) {
    // THIS request asked for the header, and the server answered with an empty
    // column list AND an empty blob: a genuine result with no columns. Both
    // halves are required — a row of zero columns encodes to zero bytes, so an
    // empty list over a NON-empty blob is a server that ignored the flag, and
    // that falls through to `needsSchema` rather than decoding as zero rows.
    header = { columns: [], types: [] };
  }

  if (!header) {
    resp.columns = [];
    resp.types = null;
    resp.data = [];
    resp.rowsBlob = blob;
    resp.needsSchema = true;
    return;
  }
  resp.columns = header.columns;
  resp.types = header.types;
  resp.data = decodeTypedBlob(header.types, blob, count);
}

/**
 * B1156: a u64 the wire wrote with `int_optimal` — a row version or a file
 * size. A Number while it is exact, a BigInt past 2^53. A version goes back to
 * the server as `read_version`, so a rounded one would be a wrong one.
 */
function toExactU64(v) {
  if (typeof v !== 'bigint') return v;
  return v <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(v) : v;
}

/**
 * B1156: fill in `resp` from the `WatchFill` array — the 0x24 payload, and the
 * second slot of 0x28.
 *
 * `Array[table, schema_id, columns, upserts, removes, token, full, more
 * [, upsert_versions [, error [, upsert_manifests [, retry
 * [, upsert_predicates [, gap]]]]]]]`. The six trailing slots are OPTIONAL (`encode_watch_fill`, kinedb-protocol `message.rs`), and
 * an absent one reads as empty. A non-empty `error` makes the frame a FAILURE
 * frame: no rows, no token, and the subscriber re-subscribes. ONE header
 * describes BOTH blobs, and the frame always carries it.
 *
 * B2200: the `token` of a frame with `more == true` is a CURSOR (`c1:…`). An
 * application that stores it resumes with `WATCH t SINCE '<cursor>'`. The last
 * frame (`more == false`) carries the plain head token.
 */
function fillWatchFill(resp, arr) {
  resp.table = arr[0];
  resp.schemaId = toSchemaId(arr[1]);
  const header = unpackColumns(arr[2] || []);
  resp.columns = header.columns;
  resp.types = header.types;
  resp.upserts = decodeTypedBlob(header.types, arr[3]);
  resp.removes = decodeTypedBlob(header.types, arr[4]);
  resp.token = arr[5];
  resp.full = arr[6];
  resp.more = arr[7];
  resp.upsert_versions = (arr[8] || []).map(toExactU64);
  resp.error = typeof arr[9] === 'string' ? arr[9] : '';
  // One item per upsert row: `null`, or `[size, hash, chunks]` of a file row.
  resp.upsert_manifests = (arr[10] || []).map(m => (m == null ? null : {
    size: toExactU64(m[0]),
    hash: m[1],
    chunks: m[2] || [],
  }));
  // B501 S8: the verdict of a FAILURE frame, from the server. The words of
  // `error` never decide it. Absent (an older server, or a terminal failure)
  // is false.
  resp.retry = arr[11] === true;
  // B2199: one list per upsert row, the ids of the predicates of the watch's
  // set that match it. Absent (a bare WHERE, or an older server) is empty.
  resp.upsert_predicates = (arr[12] || []).map(ids => ids || []);
  // B2201: the GAP answer of a watch with ALLOW GAP, or null. A gap frame has
  // no rows and its token is HEAD: the server did not send the changes after
  // `gap.since`, and the watch is live from HEAD. Absent (every other frame,
  // or an older server) is null.
  const g = arr[13];
  resp.gap = Array.isArray(g) ? {
    reason: g[0],
    since: g[1],
    limit: toExactU64(g[2]),
    changed_rows_at_least: toExactU64(g[3]),
  } : null;
}

// Map a TLV-decoded positional array onto the JS Response shape the rest of
// the UI expects (matches the old serde JSON tagging — `r.type`, `r.data`,
// `r.columns`, `r.count`, etc.).
//
// `ctx` is the B607 decode context for a typed reply: `{schemaEntry, wantSchema}`
// from the pending request that this frame answers. Absent for every other
// message type.
function retiredWatchDataMessage() {
  return 'protocol error: the server sent the retired WatchData (0x23) frame '
    + 'to a versioned client (B1514)';
}

export function buildResponse(msgType, arr, elapsedUs, ctx) {
  const resp = {};
  if (elapsedUs != null) resp.elapsed_us = elapsedUs;
  switch (msgType) {
    case MT_CREATED:
      resp.type = 'created'; break;
    case MT_INSERTED:
      resp.type = 'inserted'; resp.count = arr[0]; break;
    case MT_UPDATED:
      resp.type = 'updated';  resp.count = arr[0]; break;
    case MT_DELETED:
      resp.type = 'deleted';  resp.count = arr[0]; break;
    case MT_WATCHING:
      resp.type = 'watching'; resp.table = arr[0]; break;
    case MT_UNWATCHED:
      resp.type = 'unwatched'; resp.count = arr[0]; break;
    case MT_WATCH_NOTIFY:
      resp.type = 'watch_notify';
      resp.table = arr[0];
      resp.changed = arr[1];
      break;
    // B1514: a versioned login never receives 0x23. Refuse it; never decode
    // its lossy JSON cells as if they were data.
    case MT_WATCH_DATA_RETIRED:
      throw new KineDBError(retiredWatchDataMessage());
    // B1168 `WatchTypedData`: `Array[table, schema_id, columns, rows, changed
    // [, operation]]`. It keeps the `watch_data` shape, so a callback reads it
    // with the code it already has. It ADDS `typed`, `schemaId` and `types`.
    // The frame always carries its header, so no schema cache is involved.
    case MT_WATCH_TYPED_DATA: {
      resp.type = 'watch_data';
      resp.typed = true;
      resp.table = arr[0];
      resp.schemaId = toSchemaId(arr[1]);
      const header = unpackColumns(arr[2] || []);
      resp.columns = header.columns;
      resp.types = header.types;
      resp.changed = typeof arr[4] === 'bigint' ? Number(arr[4]) : arr[4];
      resp.data = decodeTypedBlob(header.types, arr[3]);
      if (typeof arr[5] === 'string') resp.operation = arr[5];
      break;
    }
    // B1156 `WatchFill`: the cells ride the `typed_rows` blob, as on 0x27.
    case MT_WATCH_FILL:
      resp.type = 'watch_fill';
      fillWatchFill(resp, arr);
      break;
    // B1156 `WatchTail`: `Array[base, <the WatchFill array>]`. A client applies
    // the diff ONLY when `base` is the token its local set is complete as of.
    case MT_WATCH_TAIL:
      resp.type = 'watch_tail';
      resp.base = arr[0];
      fillWatchFill(resp, arr[1] || []);
      break;
    case MT_ROWS:
      resp.type = 'rows';
      resp.columns = arr[0];
      resp.data = arr[1].map(row => row.map(decodeJsonCell));
      resp.count = arr[2];
      break;
    // B607 `TypedRows`: `Array[schema_id, columns, rows, count]`. The result
    // keeps the shape every UI consumer already indexes — `columns` stays the
    // NAME array, `data` stays row-major — and only ADDS `schemaId` and
    // `types`, so `rowsAsObjects` and every existing caller are unchanged.
    case MT_TYPED_ROWS:
      resp.type = 'rows';
      // `count` is `int_optimal`, so it is an Int64 past 2^48 and a typed
      // payload keeps an Int64 exact. Callers compare it with `===` against a
      // Number, so hand them a Number — a row count cannot exceed 2^53.
      resp.count = typeof arr[3] === 'bigint' ? Number(arr[3]) : arr[3];
      fillTypedRows(resp, arr[0], arr[1], arr[2], ctx, resp.count);
      break;
    // `TypedRowChunk`: `Array[schema_id, columns, rows]` — no count, and
    // `columns` only on the first chunk of a stream. `_onFrame` folds the
    // chunks into one `rows` result at `RowStreamEnd`.
    case MT_TYPED_ROW_CHUNK:
      resp.type = 'row_chunk';
      fillTypedRows(resp, arr[0], arr[1], arr[2], ctx, undefined);
      break;
    // B1644 `RowChunk`: `Array[columns, rows]` — the legacy chunk of a plain
    // Query reply that passes one chunk, on a connection without the typed
    // grant. `columns` rides the first chunk only. `_onFrame` folds it.
    case MT_ROW_CHUNK:
      resp.type = 'row_chunk';
      resp.columns = arr[0];
      resp.data = arr[1].map(row => row.map(decodeJsonCell));
      break;
    case MT_ROW_STREAM_END:
      // `Array[count]` — the server's running total for the whole stream.
      resp.type = 'row_stream_end';
      resp.count = typeof arr[0] === 'bigint' ? Number(arr[0]) : arr[0];
      break;
    case MT_AUTH_RESULT:
      // Positional (username, is_superuser). NO token, by design — a WS session
      // is held server-side, bound to the connection that logged in.
      resp.type = 'auth_result';
      resp.username = arr[0];
      resp.is_superuser = arr[1];
      break;
    // B971, the four file replies. Each one is a positional Array, in the
    // order `sdk/test-vectors/files-wire-v1.json` declares. A hash stays a
    // Uint8Array: the caller compares it with a blake3 digest, never prints it.
    case MT_FILE_CHUNK_PUT:
      resp.type = 'file_chunk_put';
      resp.index = typeof arr[0] === 'bigint' ? Number(arr[0]) : arr[0];
      break;
    case MT_FILE_PUT:
      resp.type = 'file_put';
      resp.size = typeof arr[0] === 'bigint' ? Number(arr[0]) : arr[0];
      break;
    case MT_FILE_MANIFEST:
      resp.type = 'file_manifest';
      resp.size = typeof arr[0] === 'bigint' ? Number(arr[0]) : arr[0];
      resp.hash = arr[1];
      resp.chunks = arr[2];
      break;
    case MT_FILE_CHUNK:
      resp.type = 'file_chunk';
      resp.bytes = arr[0];
      break;
    // B984: the skip hint reply. `held` is the bitmap; see `heldBitmapGet`.
    case MT_HELD_CHUNKS:
      if (arr[0].length > heldBitmapLen(FIND_HELD_CHUNKS_MAX)) {
        throw new KineDBError('HeldChunks bitmap is longer than FIND_HELD_CHUNKS_MAX bits');
      }
      resp.type = 'held_chunks';
      resp.held = arr[0];
      break;
    case MT_ERROR:
      resp.type = 'error';
      resp.message = arr[0];
      // Trailing positional Bool: the server's wire retry flag. Deliberately
      // LEFT UNDEFINED when absent (legacy server) — `undefined` = "the server
      // never said", `false` = "a flag-aware server said terminal". KineDBError
      // treats both as non-retryable (the safe side), but callers layering a
      // legacy fallback need the distinction on `err.response.retry`.
      if (arr.length > 1) resp.retry = !!arr[1];
      // B495: items 2 and 3, `kind_code` and `retry_ms`, are only on a typed
      // refusal. An unknown code gives no refusal, and the error stays a plain
      // retryable error. Same shape as the JSON `refusal` of `POST /sql`.
      if (arr.length > 3) {
        const kind = REFUSAL_KINDS[Number(arr[2])];
        if (kind) resp.refusal = { kind, retry_ms: Number(arr[3]) };
      }
      break;
    case MT_BACKOFF:
      resp.type = 'backoff'; resp.retry_ms = arr[0]; break;
    case MT_HEALTH_REPLY:
      resp.type = 'health';
      resp.status = arr[0];
      resp.peers = arr[1];
      resp.memory = (arr[2] || []).map(e => ({
        name: e[0], bytes: e[1], entries: e[2], can_shed: e[3],
      }));
      resp.memory_total_bytes = arr[3];
      resp.rss_bytes = arr[4];
      // B1872: slot 6 is the build revision of the node. A reply without it reads empty.
      resp.revision = arr.length > 6 ? arr[6] : '';
      break;
    default:
      resp.type = `unknown_msg_0x${msgType.toString(16).padStart(2, '0')}`;
  }
  return resp;
}

/**
 * Persistent WebSocket connection. Pipelines requests in FIFO order — the
 * server processes WS frames sequentially per connection and mirrors each
 * response's request_id from the request frame, so matching by send-order
 * is unambiguous. Watch notifications (request_id=0,
 * msg_type=0x22/0x24/0x27/0x28) arrive unsolicited and route to
 * registered callbacks by table name without consuming a request slot.
 */
export class KineDBSocket {
  /**
   * @param {WebSocket} ws - an OPEN WebSocket.
   * @param {{retry?: boolean|object, database?: string, branch?: string}} [opts]
   *   - `retry`: same shape as the KineDB constructor; `connect()` passes the
   *     client's option through.
   *   - `database` / `branch` (B1871): the default of this connection. See
   *     `setContext()`; `connect()` passes the client's default through.
   *
   * B1514: the client is always typed. A login carries
   * CLIENT_PROTOCOL_VERSION and makes the connection typed. Before a login,
   * each Query frame sets FLAG_TYPED_ROWS. The `typedRows` option is retired
   * and ignored.
   */
  constructor(ws, opts = {}) {
    this.ws = ws;
    this._retry = normalizeRetryOpts(opts?.retry);
    // B495: the clock and the sleep of the retry loop. A test replaces both.
    this._now = Date.now;
    this._sleep = sleep;
    // B971: the FILE grant, read off the `AuthResult` frame flags, and
    // whether this connection ever logged in at all. An open server answers
    // without a login, and then the grant is unknowable, so the file calls
    // check the grant ONLY after a successful login.
    this._filesGranted = false;
    // B984: the SKIP grant. Without it `putFile` sends every chunk.
    this._fileSkipGranted = false;
    // B1514: a successful versioned login is the typed grant for rows and
    // for the watch push. The server echoes no typed bit.
    this._authenticated = false;
    /**
     * The schema cache: `(sql, db, branch)` → `{schemaId, columns, types}`.
     *
     *   `schemaId` 16-digit lowercase hex, `===`-comparable
     *   `columns`  the column NAME array
     *   `types`    `[{code, name, param}]`, aligned to `columns`
     *
     * A miss puts FLAG_WANT_SCHEMA on the query; a hit sends the query bare and
     * decodes with the cached header. Public so a caller can inspect or clear
     * it; the client itself never needs to be told to invalidate, because a
     * `schemaId` mismatch already forces one re-request.
     *
     * B1754: the cache is an LRU of at most SCHEMA_CACHE_CAPACITY entries. The
     * `Map` insertion order is the LRU order: the first key is the oldest. A
     * hit moves its key to the end; an insert past the bound drops the first.
     * @type {Map<string, {schemaId:string, columns:string[], types:Array<{code:number,name:string,param:number}>}>}
     */
    this.schemaCache = new Map();
    // B1871: the default database and branch of this connection. Every Query
    // frame carries them, and they are the other half of the cache key.
    // `null` is no default: the frame has no slots, and the server uses the
    // session (`USE`).
    [this._db, this._branch] = defaultContext(opts?.database, opts?.branch);
    ws.binaryType = 'arraybuffer';
    /** @type {Array<{resolve:Function, reject:Function}>} */
    this._pending = [];
    /** @type {Map<string, Set<Function>>}  table → callbacks */
    this._watchers = new Map();
    /**
     * B2200: the `WatchFillNext` requests that wait for their page.
     * request id → table. The next push of the table removes the entry. An
     * `Error` with one of these ids answers no request of the application.
     * @type {Map<number, string>}
     */
    this._fillNext = new Map();
    this._closed = false;
    // Monotonic request_id counter. Wraps at u16 (server header field width);
    // collisions are harmless because we match by FIFO order, not by id.
    this._nextRequestId = 1;

    ws.onmessage = (evt) => this._onFrame(evt.data);
    ws.onclose = () => {
      this._closed = true;
      for (const w of this._pending) w.reject(new KineDBError('connection closed'));
      this._pending.length = 0;
      this._fillNext.clear();
    };
    ws.onerror = () => {
      // close handler will fire after error
    };
  }

  _onFrame(data) {
    // The browser will hand us either an ArrayBuffer (binary frame, our
    // normal path) or a string (the server has no text path today, but
    // guard against a stray Text frame so a `JSON.parse` failure can't
    // mask a real binary response sitting behind it).
    if (typeof data === 'string') return;

    const u8 = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
    if (u8.length < FRAME_HEADER_SIZE) return;

    // Fast-path drop: an unsolicited watch frame with no registered
    // callbacks anywhere is pure cost. The msg_type byte is the first
    // byte of the frame header and is never compressed, so this peek is
    // safe regardless of FLAG_COMPRESSED. Without it, a busy VERBOSE
    // feed would force an LZ4 decompress + TLV walk for every dropped
    // frame.
    const msgType = u8[0];
    // B1514: a 0x23 frame answers no request, so it must not take the slot of
    // the request in flight. The server broke the protocol: fail the
    // connection loudly.
    if (msgType === MT_WATCH_DATA_RETIRED) {
      this._failConnection(retiredWatchDataMessage());
      return;
    }
    // B1156: 0x24 and 0x28 are push frames too. Outside this set they took
    // the head of the pending queue and resolved an unrelated request.
    const push = msgType === MT_WATCH_NOTIFY
      || msgType === MT_WATCH_TYPED_DATA || msgType === MT_WATCH_FILL
      || msgType === MT_WATCH_TAIL;
    if (this._watchers.size === 0 && push) {
      return;
    }

    // Parse errors must NOT silently drop: a stuck request is harder to
    // debug than a clear `KineDBError`. Fail the head of the pending queue
    // (FIFO with the server's response order) so the calling promise
    // surfaces the wire-level problem instead of hanging indefinitely.
    const typed = msgType === MT_TYPED_ROWS || msgType === MT_TYPED_ROW_CHUNK;
    let frame, payload, resp;
    try {
      frame = parseFrame(u8);
      // A typed reply's `schema_id` is a 64-bit content hash: it must survive
      // the TLV decode with every bit intact, so only these two messages ask
      // for an exact Int64.
      // B1168: the typed watch push carries a `schema_id` too.
      // B1156: so do the fill and the tail push.
      const exact = typed || msgType === MT_WATCH_TYPED_DATA
        || msgType === MT_WATCH_FILL || msgType === MT_WATCH_TAIL;
      [payload] = decodeTlv(frame.payload, 0, exact ? { exactInt64: true } : undefined);
      // The pending HEAD is the request this frame answers (the server replies
      // in send-order), and it carries what the typed decoder needs: the cached
      // column header for this query shape, and whether we asked for the schema.
      resp = buildResponse(frame.msgType, payload, frame.elapsedUs,
        typed ? this._pending[0] : undefined);
    } catch (e) {
      console.warn('[kinedb] WS frame parse failed:', e?.message || e, 'len=', u8.length, 'msg_type=0x' + msgType.toString(16));
      // B1168: a push answers no request, so its failure must not reject the
      // request that is in flight. The warning above reports it.
      if (push) return;
      const w = this._pending.shift();
      if (w) w.reject(new KineDBError(`WS parse failed: ${e?.message || e}`));
      return;
    }

    // B971: the file grants ride the AuthResult frame flags. Read them before
    // the reply resolves, so the first file call already knows.
    if (frame.msgType === MT_AUTH_RESULT) {
      this._filesGranted = (frame.flags & FLAG_FILES) !== 0;
      // B984: so does the skip grant.
      this._fileSkipGranted = (frame.flags & FLAG_FILE_SKIP) !== 0;
      this._authenticated = true;
    }

    // Watch notifications: route to subscribers, do not consume a pending
    // request slot. B1156: the decision is the push set above, by opcode, so
    // a fill (`watch_fill`) and a tail diff (`watch_tail`) route here too.
    if (push) {
      const paged = msgType === MT_WATCH_FILL || msgType === MT_WATCH_TAIL;
      // B2200: a page of the table arrived, so the server read every
      // `WatchFillNext` of that table.
      if (paged) {
        for (const [rid, t] of this._fillNext) if (t === resp.table) this._fillNext.delete(rid);
      }
      const cbs = this._watchers.get(resp.table);
      const taken = [];
      if (cbs) for (const cb of cbs) taken.push(cb(resp));
      // B2200: a page with `more` waits for `WatchFillNext`. Ask after the
      // application took the page. A zero-row page asks too.
      if (paged && resp.more === true && cbs) this._askNextPage(resp.table, taken);
      return;
    }

    // B2200: the `Error` of a `WatchFillNext` (no fill of the table is in
    // progress). It answers no request of the application, so it must not
    // take the slot of the request in flight.
    if (resp.type === 'error' && this._fillNext.has(frame.requestId)) {
      const table = this._fillNext.get(frame.requestId);
      this._fillNext.delete(frame.requestId);
      console.warn(`[kinedb] WatchFillNext for ${table} refused: ${resp.message}`);
      return;
    }

    // A row chunk is one instalment of a stream: accumulate it on the pending
    // entry and keep the slot until `RowStreamEnd` arrives.
    if (resp.type === 'row_chunk') {
      const head = this._pending[0];
      if (!head) return;
      if (resp.needsSchema) {
        head.chunks = null;
        this._resendForSchema(this._pending.shift());
        return;
      }
      if (!head.chunks) {
        head.chunks = { schemaId: resp.schemaId, columns: resp.columns, types: resp.types, data: [] };
        // Only the FIRST chunk of a stream carries the header. It supersedes
        // whatever the cache held for the rest of THIS stream — the later
        // chunks arrive with an empty column list and would otherwise look
        // like a schema the client has never seen.
        head.schemaEntry = head.chunks;
      }
      for (const row of resp.data) head.chunks.data.push(row);
      return;
    }

    const w = this._pending.shift();
    if (!w) return; // stray frame; ignore

    if (resp.type === 'row_stream_end') {
      const acc = w.chunks || { schemaId: null, columns: [], types: null, data: [] };
      // The terminal frame carries the server's total for the whole stream.
      // A disagreement means chunks went missing, and a short result set that
      // LOOKS complete is the worst outcome available — fail loudly instead.
      if (resp.count != null && resp.count !== acc.data.length) {
        w.reject(new KineDBError(
          `row stream ended at ${acc.data.length} rows, server counted ${resp.count}`));
        return;
      }
      this._cacheSchema(w, acc);
      w.resolve({
        type: 'rows',
        schemaId: acc.schemaId,
        columns: acc.columns,
        types: acc.types,
        data: acc.data,
        count: acc.data.length,
      });
      return;
    }

    // B607: the reply is typed but we cannot decode it — no schema on the wire,
    // and no cache entry for THIS `schemaId` (the shape changed under an
    // `ALTER TABLE`, or this connection never saw the header). Re-run the same
    // query ONCE asking for the schema. Never guess with a stale header.
    if (resp.needsSchema) {
      this._resendForSchema(w);
      return;
    }
    this._cacheSchema(w, resp);

    // Server-requested backoff: sleep `retry_ms`, then resend the SAME
    // request frame and re-queue the pending entry at the BACK of the
    // queue. The server processes WS frames in send-order, so re-queueing
    // matches the (now later) wire position of the resend. We cap retries
    // at WS_BACKOFF_MAX_RETRIES so a stuck server doesn't make the call
    // hang indefinitely.
    if (resp.type === 'backoff') {
      const retryMs = Number(resp.retry_ms) || 0;
      w.backoffCount = (w.backoffCount || 0) + 1;
      if (w.backoffCount > WS_BACKOFF_MAX_RETRIES) {
        w.reject(new KineDBError(`server backoff: too many retries (${WS_BACKOFF_MAX_RETRIES})`));
        return;
      }
      setTimeout(() => {
        if (this._closed) {
          w.reject(new KineDBError('connection closed'));
          return;
        }
        this._pending.push(w);
        try { this.ws.send(w.frame); }
        catch (e) {
          this._pending.pop();
          w.reject(new KineDBError(`ws.send failed during backoff retry: ${e?.message || e}`));
        }
      }, retryMs);
      return;
    }

    w.resolve(resp);
  }

  /**
   * Re-send a pending request with FLAG_WANT_SCHEMA, ONCE.
   *
   * The same `request_id` and the same payload — only the flags byte differs —
   * and the entry goes to the BACK of the queue, matching the resend's new wire
   * position, exactly as the backoff retry does. A second miss means the server
   * ignored the flag, and we reject instead of looping for ever.
   */
  _resendForSchema(w) {
    if (!w) return;
    if (w.schemaRetried) {
      w.reject(new KineDBError(
        'server sent a typed result set with no schema after FLAG_WANT_SCHEMA'));
      return;
    }
    w.schemaRetried = true;
    w.wantSchema = true;
    w.frame = buildFrame(w.msgType, w.requestId, w.payload, FLAG_WANT_SCHEMA);
    if (this._closed) { w.reject(new KineDBError('connection closed')); return; }
    this._pending.push(w);
    try { this.ws.send(w.frame); }
    catch (e) {
      this._pending.pop();
      w.reject(new KineDBError(`ws.send failed during schema re-request: ${e?.message || e}`));
    }
  }

  /**
   * Remember the column header this reply resolved, keyed by query shape.
   * B1754: the entry goes to the newest place, and an insert past
   * SCHEMA_CACHE_CAPACITY drops the oldest entry.
   */
  _cacheSchema(w, resp) {
    if (!w?.schemaKey || !resp?.types || resp.schemaId == null) return;
    // `Map.set` on a present key keeps its old place. Delete it first.
    this.schemaCache.delete(w.schemaKey);
    this.schemaCache.set(w.schemaKey, {
      schemaId: resp.schemaId,
      columns: resp.columns,
      types: resp.types,
    });
    while (this.schemaCache.size > SCHEMA_CACHE_CAPACITY) {
      this.schemaCache.delete(this.schemaCache.keys().next().value);
    }
  }

  /**
   * B1754: look up a query shape and, on a hit, move it to the newest place.
   * The lookup is the use: the request captures the entry here, and the
   * reply decodes with that captured entry. An eviction between the request
   * and the reply thus cannot break the decode.
   */
  _lookupSchema(key) {
    const entry = this.schemaCache.get(key);
    if (!entry) return null;
    this.schemaCache.delete(key);
    this.schemaCache.set(key, entry);
    return entry;
  }

  /**
   * Set the default database and branch of this connection (B1871).
   *
   * Every Query frame then carries them, and they key the schema cache. A
   * database without a branch uses `main`. No argument clears the default:
   * the frames carry no target, and the server uses the session (`USE`). A
   * branch without a database throws a TypeError.
   *
   * A qualified table name in the SQL (`db.t`, `db@branch.t`) still wins. The
   * server refuses `USE` on a connection with a default, because the default
   * wins over the session.
   */
  setContext({ db, branch } = {}) {
    [this._db, this._branch] = defaultContext(db, branch);
  }

  /** The schema-cache key of `sql` on this connection's default. */
  _schemaKey(sql) {
    return schemaCacheKey(sql, this._db ?? '', this._branch ?? '');
  }

  /**
   * True once a login succeeded. B1514: a versioned login gets the typed
   * result set on every reply. A guest gets it too, per Query frame.
   */
  get typedGranted() { return this._authenticated; }

  /** True once a login succeeded. B1514: a versioned login gets 0x27 only. */
  get typedWatchGranted() { return this._authenticated; }

  /** True once the server has GRANTED the files family on this connection. */
  get filesGranted() { return this._filesGranted; }

  /** True once the server has GRANTED the skip hint (0x38) on this connection. */
  get fileSkipGranted() { return this._fileSkipGranted; }

  // ── B971: files of any size ───────────────────────────────────────────────

  /**
   * Store `data` as the file `name` of `collection`.
   *
   * The client cuts the bytes at CONTENT boundaries, hashes each chunk with
   * blake3 and sends one `PutFileChunk` (0x30) per chunk, at most
   * `FILE_UPLOAD_WINDOW` unanswered. It then sends one `PutFile` (0x32) with
   * the size, the blake3 of the whole file and the chunk hash list. A file has
   * NO size limit by design.
   *
   * B984: when the server echoed `FLAG_FILE_SKIP`, the client first asks
   * `FindHeldChunks` (0x38) for the distinct hashes. It then sends only the
   * chunks the shard does not hold, and each distinct hash only once. The
   * hint is not proof: when the server refuses the manifest after a skip, the
   * client sends every skipped chunk and the manifest once more, and throws a
   * second refusal. A timeout or a lost connection is never retried. Without
   * the echo the client sends every chunk.
   *
   * Run ONE file call at a time on a socket. A query in between is safe,
   * because every reply matches by FIFO order, but two uploads at once
   * interleave their chunk frames for no gain.
   *
   * @param {string} collection
   * @param {string} name
   * @param {Uint8Array} data
   * @param {{db?:string, branch?:string, timeoutMs?:number}} [opts]
   * @returns {Promise<{size:number, hash:string, chunks:string[], chunkBytesSent:number}>}
   *   hashes hex. `chunkBytesSent` counts the chunk bytes this call really
   *   sent, so a file the shard already holds reports 0.
   */
  async putFile(collection, name, data, opts = {}) {
    this._requireFiles();
    const budget = opts.timeoutMs ?? FILE_REQUEST_TIMEOUT_MS;
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data || 0);
    const whole = blake3Hash(bytes);
    const pieces = cdcChunks(bytes);
    const hashes = pieces.map((piece) => blake3Hash(piece));
    const where = { db: opts.db, branch: opts.branch, collection, name };

    // The first pass. Without the grant it is every index.
    let plan;
    if (this._fileSkipGranted) {
      const hexes = hashes.map(hexOf);
      const distinct = [];
      const firstSeen = new Set();
      hexes.forEach((h, i) => {
        if (!firstSeen.has(h)) { firstSeen.add(h); distinct.push(hashes[i]); }
      });
      // FIND_HELD_CHUNKS_MAX is a multiple of 8, so the bitmaps join with no gap.
      const held = new Uint8Array(heldBitmapLen(distinct.length));
      for (let start = 0; start < distinct.length; start += FIND_HELD_CHUNKS_MAX) {
        const part = distinct.slice(start, start + FIND_HELD_CHUNKS_MAX);
        held.set(await this._findHeldChunks(where, part, budget), start / 8);
      }
      plan = planChunkSends(hexes, held);
    } else {
      plan = pieces.map((_p, i) => i);
    }
    let chunkBytesSent = await this._sendChunks(where, pieces, hashes, plan, budget);

    const manifest = {
      type: 'put_file', ...where, size: bytes.length, hash: whole, chunks: hashes,
    };
    // B2298: a hold refusal of the manifest resends the manifest frame only.
    let resp = await this._fileSendRaw(manifest, MT_PUT_FILE, budget);
    if (resp?.type === 'error' && plan.length < pieces.length) {
      // A refusal after a skip: the hint was stale, or a repeated hash was not
      // covered. Send every skipped chunk with its bytes, then the manifest
      // once more. A second refusal is the answer.
      const planned = new Set(plan);
      const rest = pieces.map((_p, i) => i).filter((i) => !planned.has(i));
      chunkBytesSent += await this._sendChunks(where, pieces, hashes, rest, budget);
      resp = await this._fileSendRaw(manifest, MT_PUT_FILE, budget);
    }
    resp = unwrap(resp);
    if (resp?.type !== 'file_put') {
      throw new KineDBError(`the server answered ${resp?.type} to ${fileMsgName(MT_PUT_FILE)}`);
    }
    if (resp.size !== bytes.length) {
      throw new KineDBError(
        `putFile: the server recorded ${resp.size} bytes for \`${collection}\`.\`${name}\`, `
        + `the client sent ${bytes.length}`);
    }
    return {
      size: bytes.length,
      hash: hexOf(whole),
      chunks: hashes.map(hexOf),
      chunkBytesSent,
    };
  }

  /**
   * B1823: ask the server which chunks of the file `name` of `collection` it
   * holds. Return one flag for each hash, in the order of `hashes`.
   *
   * `hashes` are the hex chunk hashes that `putFile` answers. The client asks
   * `FindHeldChunks` (0x38), at most `FIND_HELD_CHUNKS_MAX` hashes in one
   * request. A file page uses the answer to show which chunks of a file that
   * a push did not finish are on the server. The answer is a hint, the same
   * one that `putFile` reads: a caller without the blanket read of the
   * collection gets `false` for every hash.
   *
   * @param {string} collection
   * @param {string} name
   * @param {string[]} hashes 64 hex digits each.
   * @param {{db?:string, branch?:string, timeoutMs?:number}} [opts]
   * @returns {Promise<boolean[]>}
   * @throws {KineDBError} when the server did not grant the file skip
   *   (`FLAG_FILE_SKIP`) at login. An old server does not answer 0x38.
   */
  async heldChunks(collection, name, hashes, opts = {}) {
    const list = Array.from(hashes ?? []);
    const raw = list.map((hex) => {
      if (typeof hex !== 'string' || !/^[0-9a-fA-F]{64}$/.test(hex)) {
        throw new KineDBError(`heldChunks: a chunk hash must be 64 hex digits, not ${JSON.stringify(hex)}`);
      }
      return Uint8Array.from(hex.match(/../g), (b) => parseInt(b, 16));
    });
    if (raw.length === 0) return [];
    if (!this._fileSkipGranted) {
      throw new KineDBError(
        'heldChunks: the server did not grant the file skip (FLAG_FILE_SKIP) at login, '
        + 'so it does not answer FindHeldChunks (0x38)');
    }
    const where = { db: opts.db, branch: opts.branch, collection, name };
    const budget = opts.timeoutMs ?? FILE_REQUEST_TIMEOUT_MS;
    const out = [];
    for (let start = 0; start < raw.length; start += FIND_HELD_CHUNKS_MAX) {
      const part = raw.slice(start, start + FIND_HELD_CHUNKS_MAX);
      const bitmap = await this._findHeldChunks(where, part, budget);
      part.forEach((_h, i) => out.push(heldBitmapGet(bitmap, i)));
    }
    return out;
  }

  /** B984: ask which of `hashes` the shard holds. Return the bitmap. */
  async _findHeldChunks(where, hashes, timeoutMs) {
    const resp = await this._fileRequest(
      { type: 'find_held_chunks', ...where, hashes },
      MT_FIND_HELD_CHUNKS, timeoutMs, 'held_chunks');
    const want = heldBitmapLen(hashes.length);
    if (resp.held.length !== want) {
      throw new KineDBError(
        `findHeldChunks: the server sent a bitmap of ${resp.held.length} bytes for `
        + `${hashes.length} hashes, the client expected ${want}`);
    }
    return resp.held;
  }

  /**
   * Send the chunks at `indexes`, at most `FILE_UPLOAD_WINDOW` unanswered.
   * Return the chunk bytes sent.
   */
  async _sendChunks(where, pieces, hashes, indexes, timeoutMs) {
    const window = [];
    let sent = 0;
    try {
      for (const index of indexes) {
        const req = {
          type: 'put_file_chunk', ...where,
          index, hash: hashes[index], bytes: pieces[index],
        };
        const pending = this._send(req);
        sent += pieces[index].length;
        window.push({ index, req, pending });
        if (window.length >= FILE_UPLOAD_WINDOW) await this._takeChunkAck(window, timeoutMs);
      }
      while (window.length) await this._takeChunkAck(window, timeoutMs);
    } catch (e) {
      // A refusal in the middle of the window leaves replies the socket still
      // owes. Consume them, so a later request does not take one as its own,
      // then rethrow the FIRST error, which is the truthful one.
      await this._drainWindow(window, timeoutMs);
      throw e;
    }
    return sent;
  }

  /**
   * The manifest of one file: `{size, hash, chunks}`, every hash lower-case hex.
   * @param {{db?:string, branch?:string, timeoutMs?:number}} [opts]
   */
  async fileInfo(collection, name, opts = {}) {
    this._requireFiles();
    const budget = opts.timeoutMs ?? FILE_REQUEST_TIMEOUT_MS;
    const resp = await this._fileRequest({
      type: 'get_file', db: opts.db, branch: opts.branch, collection, name,
    }, MT_GET_FILE, budget, 'file_manifest');
    return {
      size: resp.size,
      hash: hexOf(resp.hash),
      chunks: resp.chunks.map(hexOf),
    };
  }

  /**
   * Read one file back, verified, as a `Uint8Array`.
   *
   * Every chunk is checked against the hash the manifest named, and the
   * assembled file against the manifest's whole-file hash. A mismatch throws
   * and NO bytes are returned: a caller never sees a file that was only
   * partly verified.
   *
   * @param {{db?:string, branch?:string, timeoutMs?:number}} [opts]
   */
  async getFile(collection, name, opts = {}) {
    const budget = opts.timeoutMs ?? FILE_REQUEST_TIMEOUT_MS;
    const manifest = await this.fileInfo(collection, name, opts);

    const bodies = [];
    let total = 0;
    for (let index = 0; index < manifest.chunks.length; index += 1) {
      const wantHex = manifest.chunks[index];
      const resp = await this._fileRequest({
        type: 'get_file_chunk',
        db: opts.db, branch: opts.branch, collection, name,
        hash: fromHex32(wantHex),
      }, MT_GET_FILE_CHUNK, budget, 'file_chunk');
      const body = resp.bytes instanceof Uint8Array ? resp.bytes : new Uint8Array(resp.bytes || 0);
      const got = blake3HashHex(body);
      if (got !== wantHex) {
        throw new KineDBError(
          `getFile: chunk ${index} of \`${collection}\`.\`${name}\` does not match its `
          + `manifest hash: the manifest says ${wantHex}, the bytes hash to ${got}`);
      }
      bodies.push(body);
      total += body.length;
    }

    if (total !== manifest.size) {
      throw new KineDBError(
        `getFile: \`${collection}\`.\`${name}\` assembled to ${total} bytes, the manifest `
        + `says ${manifest.size}`);
    }
    const out = new Uint8Array(total);
    let at = 0;
    for (const body of bodies) { out.set(body, at); at += body.length; }
    const got = blake3HashHex(out);
    if (got !== manifest.hash) {
      throw new KineDBError(
        `getFile: \`${collection}\`.\`${name}\` does not match its whole-file hash: `
        + `the manifest says ${manifest.hash}, the bytes hash to ${got}`);
    }
    return out;
  }

  /**
   * Refuse a file call on a LOGGED-IN socket with no grant. A socket that
   * never authenticated is not checked: an open server answers without a
   * login, and then the grant was never on the wire.
   */
  _requireFiles() {
    if (this._authenticated && !this._filesGranted) {
      throw new KineDBError('this server has no file support (FLAG_FILES not granted)');
    }
  }

  /**
   * B2298: send one file frame and await its reply under its own budget.
   * The retry loop of the socket (`_retryReply`) sends THIS frame again after
   * a refusal it may retry. Return an error reply instead of throwing.
   */
  async _fileSendRaw(req, msgType, timeoutMs) {
    const send = () => this._fileReplyRaw(this._send(req), msgType, timeoutMs);
    return this._retryReply(await send(), send, this._retry);
  }

  /** Send one file request and return the reply of the expected shape. */
  async _fileRequest(req, msgType, timeoutMs, wantType) {
    const resp = unwrap(await this._fileSendRaw(req, msgType, timeoutMs));
    if (resp?.type !== wantType) {
      throw new KineDBError(`the server answered ${resp?.type} to ${fileMsgName(msgType)}`);
    }
    return resp;
  }

  /** Await one reply under its own budget. Name the opcode on a timeout. */
  async _fileReply(pending, msgType, timeoutMs) {
    return unwrap(await this._fileReplyRaw(pending, msgType, timeoutMs));
  }

  /** Like `_fileReply`, but return an error reply instead of throwing. */
  async _fileReplyRaw(pending, msgType, timeoutMs) {
    let timer;
    const guard = new Promise((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new KineDBError(
          `no reply to ${fileMsgName(msgType)} within ${timeoutMs}ms`)),
        timeoutMs,
      );
    });
    // A lost race leaves `pending` unsettled. Swallow its later rejection, so
    // a socket that closes afterwards raises no unhandled rejection.
    pending.catch(() => {});
    try {
      return await Promise.race([pending, guard]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Read ONE chunk ack and check it names the chunk at the head. B2298: a
   * refusal that the retry loop may retry sends THIS chunk frame again, and
   * only this one. The later chunks of the window stay in flight; each reply
   * stays with its own frame, so the index check below still holds.
   */
  async _takeChunkAck(window, timeoutMs) {
    const { index, req, pending } = window.shift();
    const resend = () => this._fileReplyRaw(this._send(req), MT_PUT_FILE_CHUNK, timeoutMs);
    const first = await this._fileReplyRaw(pending, MT_PUT_FILE_CHUNK, timeoutMs);
    const resp = unwrap(await this._retryReply(first, resend, this._retry));
    if (resp?.type !== 'file_chunk_put') {
      throw new KineDBError(
        `the server answered ${resp?.type} to ${fileMsgName(MT_PUT_FILE_CHUNK)}`);
    }
    if (resp.index !== index) {
      throw new KineDBError(
        `putFile: the server acked chunk ${resp.index}, the client expected ${index}`);
    }
  }

  /** Consume the replies still owed. A drain that fails stops. */
  async _drainWindow(window, timeoutMs) {
    while (window.length) {
      const { pending } = window.shift();
      try {
        await this._fileReply(pending, MT_PUT_FILE_CHUNK, timeoutMs);
      } catch {
        return; // the connection is already lost
      }
    }
  }

  _send(req) {
    if (this._closed) return Promise.reject(new KineDBError('connection closed'));
    // Query and Authenticate are the only two requests reachable from a browser
    // — gossip, raft, sync and the slice-fetch RPCs are peer-to-peer. Still a
    // whitelist, not a deleted check: this is what keeps a typo'd request type
    // from being encoded as a Query.
    let msgType;
    let payload;
    let flags = 0;
    let schemaKey = null;
    let schemaEntry = null;
    // B1871: a file request that names no database and no branch goes to the
    // default of the connection, as a Query does.
    if (req?.type !== 'query' && req?.type !== 'authenticate' && req?.type !== 'transaction'
        && req?.db == null && req?.branch == null) {
      req = { ...req, db: this._db, branch: this._branch };
    }
    // B2174: the transaction payload is encoded by the caller, with its own
    // db and branch. The reply has no rows, so the frame has no flags.
    if (req?.type === 'transaction') {
      msgType = MT_TRANSACTION;
      payload = req.payload;
    } else if (req?.type === 'query') {
      msgType = MT_QUERY;
      payload = queryPayload(req.sql, this._db, this._branch, req.params ?? null);
      // B1514: a login makes the connection typed. A guest has no login, so
      // its Query asks for typed rows with the per-request bit. The server
      // then sends the full header on each guest reply. A logged-in Query
      // sends no 0x04, so FLAG_WANT_SCHEMA alone decides the header.
      if (!this._authenticated) flags |= FLAG_TYPED_ROWS;
      schemaKey = this._schemaKey(req.sql);
      schemaEntry = this._lookupSchema(schemaKey);
      // Ask for the header only when we cannot already decode this shape.
      if (!schemaEntry) flags |= FLAG_WANT_SCHEMA;
    } else if (req?.type === 'authenticate') {
      msgType = MT_AUTHENTICATE;
      // B1514: the payload carries CLIENT_PROTOCOL_VERSION. No typed bit.
      payload = encodeAuthenticatePayload(req.username, req.password, req.token);
      // B971: ask for the files family too. An old server ignores an unknown
      // flag bit, so the ask costs nothing.
      flags |= FLAG_FILES;
      // B984: ask for the skip hint too. An old server ignores the bit.
      flags |= FLAG_FILE_SKIP;
    // B971, the four file requests. Each one is its own frame, and the reply
    // matches by FIFO order like every other request on this socket.
    } else if (req?.type === 'put_file_chunk') {
      msgType = MT_PUT_FILE_CHUNK;
      payload = encodePutFileChunkPayload(
        req.db, req.branch, req.collection, req.name, req.index, req.hash, req.bytes,
      );
    } else if (req?.type === 'put_file') {
      msgType = MT_PUT_FILE;
      payload = encodePutFilePayload(
        req.db, req.branch, req.collection, req.name, req.size, req.hash, req.chunks,
      );
    } else if (req?.type === 'get_file') {
      msgType = MT_GET_FILE;
      payload = encodeGetFilePayload(req.db, req.branch, req.collection, req.name);
    } else if (req?.type === 'get_file_chunk') {
      msgType = MT_GET_FILE_CHUNK;
      payload = encodeGetFileChunkPayload(
        req.db, req.branch, req.collection, req.name, req.hash,
      );
    // B984: the skip hint.
    } else if (req?.type === 'find_held_chunks') {
      msgType = MT_FIND_HELD_CHUNKS;
      payload = encodeFindHeldChunksPayload(
        req.db, req.branch, req.collection, req.name, req.hashes,
      );
    } else {
      return Promise.reject(new KineDBError(`unsupported request type: ${req?.type}`));
    }
    const requestId = this._nextRequestId;
    this._nextRequestId = (this._nextRequestId + 1) & 0xFFFF;
    const frame = buildFrame(msgType, requestId, payload, flags);
    return new Promise((resolve, reject) => {
      // We stash `frame` on the pending entry so `_onFrame` can resend it
      // on `Response::Backoff` without re-encoding. `backoffCount` is
      // lazily initialised inside the retry path. `msgType`/`payload`/
      // `requestId` let the schema re-request rebuild the SAME frame with
      // FLAG_WANT_SCHEMA added.
      this._pending.push({
        resolve, reject, frame,
        msgType, payload, requestId,
        schemaKey, schemaEntry,
        wantSchema: (flags & FLAG_WANT_SCHEMA) !== 0,
      });
      try { this.ws.send(frame); }
      catch (e) {
        this._pending.pop();
        reject(new KineDBError(`ws.send failed: ${e?.message || e}`));
      }
    });
  }

  /**
   * Run a SQL statement and resolve with the parsed Response.
   * Throws KineDBError on `{type:'error'}` replies.
   *
   * Backoff frames are absorbed at the frame layer (`_onFrame`) regardless of
   * options — that has always been this transport's behavior. The retry
   * option additionally absorbs server-FLAGGED transient errors (the trailing
   * retry Bool on a TLV error: leader flip, split window) with a random
   * full-jitter wait before re-sending, up to `maxRetries` times. A split
   * write hold refusal uses the `holdBudgetMs` time budget instead (B495).
   * With no `retry` option, only a typed timed refusal (`write_hold`,
   * `not_ready`) is re-sent, on that budget (B2298). `retry: false` turns it off.
   *
   * B2171: `params` are the values of `$1`, `$2`, … (see `encodeParams`). The
   * server binds them, so a value is never parsed as SQL. A value outside the
   * type table is refused here, before anything goes on the wire.
   */
  async sql(sql, params) {
    return this._sqlP(sql, encodeParams(params));
  }

  /**
   * B2173: call the function `name` with typed arguments, on this
   * connection. The signature cache belongs to this socket. See
   * `KineDB.call()`.
   */
  call(name, args) {
    this._calls ??= new FunctionCalls({
      run: (sql, p) => this._sqlP(sql, p),
      makeError: (message, response) => new KineDBError(message, response),
    });
    return this._calls.call(name, args);
  }

  /** `sql()` with P already encoded (`null` for no params). */
  async _sqlP(sql, p) {
    return this._sendRetried({ type: 'query', sql, params: p }, this._retry);
  }

  /**
   * B2174: ONE complete transaction in ONE frame (`Transaction`, 0x1E). The
   * server commits all the statements or none of them. No transaction stays
   * open on the server, so two calls on one connection never mix.
   *
   * @param {Array<string|[string]|[string, Array]>} statements in order. The
   *   SDK puts consecutive statements with the same text and types in one
   *   group; the order never changes.
   * @param {{token?: string, retry?: boolean|object, db?: string, branch?: string}} [options]
   *   `token` is the commit token (B1472). `retry` has the shape of the
   *   connection option and wins over it; a retry sends the WHOLE frame again,
   *   never a part. `db` and `branch` win over the default of the connection.
   * @returns the `created` response, or throws a KineDBError.
   */
  async transaction(statements, options = {}) {
    const payload = encodeTransactionPayload(statements, {
      db: options.db ?? this._db,
      branch: options.branch ?? this._branch,
      token: options.token,
    });
    const retry = options.retry === undefined ? this._retry : normalizeRetryOpts(options.retry);
    return this._sendRetried({ type: 'transaction', payload }, retry);
  }

  /** Send `req`, and send it again after a flagged transient error. */
  async _sendRetried(req, retry) {
    const startedMs = this._now();
    const resend = () => this._send(req);
    return unwrap(await this._retryReply(await resend(), resend, retry, startedMs));
  }

  /**
   * The retry loop of this socket. `resp` is the reply to the first send.
   * While `retry` lets the loop retry `resp`, wait and call `resend` once, and
   * take its reply. Return the last reply; an error reply is not thrown.
   * `resend` sends the SAME one frame again (B2298: a file upload resends
   * only the refused chunk or manifest frame, never the whole upload).
   */
  async _retryReply(resp, resend, retry, startedMs = this._now()) {
    for (let attempt = 0; ;) {
      if (resp?.type === 'error' && resp.retry === true && retryAllows(retry, resp)) {
        const next = nextRetryDelay(resp, attempt, startedMs, this._now(), retry);
        if (next) {
          await this._sleep(next.delayMs);
          attempt = next.attempt;
          resp = await resend(); // _send rejects on its own if the socket closed meanwhile
          continue;
        }
      }
      return resp;
    }
  }

  /**
   * B05 slice 7: log in on THIS connection (`Authenticate`, `0x18`).
   *
   * Must be re-sent after EVERY reconnect: sessions are node-local, they are
   * bound to the connection, and a fresh socket starts with no identity.
   *
   * **The timeout is not optional.** A server that predates `0x18` does not
   * reply at all — `decode_request` errors, the frame is dropped as
   * `[WS_PARSE_DROP]`, and nothing is written back — so without a receive budget
   * the login promise never settles and the form hangs with no error to show.
   * Same reason `kinedb-protocol`'s `WsClient::authenticate` carries its own.
   *
   * Resolves with `{type:'auth_result', username, is_superuser}` — no token, by
   * design.
   */
  async authenticate(username, password, opts = {}) {
    return this._authenticate({ type: 'authenticate', username, password }, opts);
  }

  /**
   * Re-auth THIS connection with a bearer `POST /login` minted
   * (`Authenticate{token}`, the trailing optional field). The server binds the
   * connection to that session without owning it, so the same token is good on
   * the next reconnect and on `POST /sql`. Same reply and timeout as
   * `authenticate`; a refusal is `session expired or revoked: log in again`.
   */
  async authenticateWithToken(token, opts = {}) {
    return this._authenticate({ type: 'authenticate', username: '', password: '', token }, opts);
  }

  async _authenticate(req, opts) {
    const timeoutMs = opts.timeoutMs ?? WS_LOGIN_TIMEOUT_MS;
    const pending = this._send(req);
    let timer;
    const guard = new Promise((_resolve, reject) => {
      timer = setTimeout(
        // Never echo the password into a message a UI will render.
        () => reject(new KineDBError(`WS login timed out after ${timeoutMs}ms`)),
        timeoutMs,
      );
    });
    try {
      return unwrap(await Promise.race([pending, guard]));
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Subscribe to changes on a table. Multiple callbacks per table are allowed.
   * Resolves with the server's Watching ack; the returned object has a
   * `.cancel()` that unsubscribes that single callback (and sends UNWATCH
   * if no callbacks remain for the table).
   *
   * B2200: a fill page with `more == true` waits. The client asks for the
   * next page after every callback returned. A callback that returns a promise
   * delays the ask until the promise resolves; a throw or a rejection stops
   * the fill (the server then sends a retryable failure frame).
   *
   * @param {string} table
   * @param {(notify: object) => void | Promise<void>} callback
   * @param {{verbose?:boolean, fast?:boolean, where?:string}} [opts]
   */
  async watch(table, callback, opts = {}) {
    const verbose = opts.verbose ? ' VERBOSE' : '';
    const fast    = opts.fast    ? ' FAST'    : '';
    const where   = opts.where   ? ` WHERE ${opts.where}` : '';
    const ack = await this.sql(`WATCH ${table}${verbose}${fast}${where}`);

    let set = this._watchers.get(table);
    if (!set) { set = new Set(); this._watchers.set(table, set); }
    set.add(callback);

    return {
      ack,
      cancel: async () => {
        const s = this._watchers.get(table);
        if (!s) return;
        s.delete(callback);
        if (s.size === 0) {
          this._watchers.delete(table);
          try { await this.sql(`UNWATCH ${table}`); } catch { /* connection may be closing */ }
        }
      },
    };
  }

  /**
   * B2200: ask for the next page of a paced fill of `table`. `taken` holds
   * what each callback returned for the page. The client waits for every
   * promise in it. A callback that throws or rejects did not take the page:
   * the client does not ask, so the server never sends a page past a page the
   * application lost. The idle fill then ends with a retryable failure frame,
   * and the application resumes from the last cursor it stored.
   */
  _askNextPage(table, taken) {
    const waits = taken.filter(r => r != null && typeof r.then === 'function');
    if (waits.length === 0) {
      this._sendFillNext(table);
      return;
    }
    Promise.all(waits).then(
      () => this._sendFillNext(table),
      (e) => console.warn(`[kinedb] a watch callback for ${table} rejected a fill page; `
        + `the client does not ask for the next page: ${e?.message || e}`),
    );
  }

  _sendFillNext(table) {
    // A closed socket, or a watch that the application cancelled in the
    // meantime: nobody takes the next page.
    if (this._closed || !this._watchers.has(table)) return;
    const requestId = this._nextRequestId;
    this._nextRequestId = (this._nextRequestId + 1) & 0xFFFF;
    this._fillNext.set(requestId, table);
    try {
      this.ws.send(buildFrame(MT_WATCH_FILL_NEXT, requestId, encodeWatchFillNextPayload(table)));
    } catch (e) {
      this._fillNext.delete(requestId);
      console.warn(`[kinedb] ws.send of WatchFillNext for ${table} failed: ${e?.message || e}`);
    }
  }

  close() {
    this._closed = true;
    try { this.ws.close(); } catch { /* already closed */ }
  }

  /**
   * B1514: the server broke the protocol. Reject every pending request with
   * the reason, then close. The FIFO match of replies is no longer safe.
   */
  _failConnection(message) {
    console.warn('[kinedb]', message);
    const err = new KineDBError(message);
    const pending = this._pending.splice(0);
    for (const w of pending) w.reject(err);
    this.close();
  }

  get connected() {
    return this.ws.readyState === 1 && !this._closed;
  }
}

/**
 * The schema-cache key. A query SHAPE is `(sql, db, branch)`: the same SQL text
 * against a different database or branch can return different columns, so it
 * must not share a cache entry. The separator is an escaped NUL, which cannot
 * occur in an identifier or in SQL text, so no two different triples can join
 * to the same key.
 */
export function schemaCacheKey(sql, db = '', branch = '') {
  return `${db}\u0000${branch}\u0000${sql}`;
}

/**
 * B1871: check a default `(database, branch)` pair and fill in its branch.
 * Returns `[null, null]` for no default, else `[db, branch]` with `branch`
 * set to `main` when it is not given: `db.t` is `db` on `main`. A branch
 * without a database, and an empty name, throw a TypeError.
 */
function defaultContext(db, branch) {
  if (db == null) {
    if (branch != null) {
      throw new TypeError(
        `a default branch (${JSON.stringify(branch)}) needs a default database: pass database (or db) too`);
    }
    return [null, null];
  }
  const d = String(db);
  const b = branch == null ? 'main' : String(branch);
  if (!d || !b) throw new TypeError('a default database or branch name must not be empty');
  return [d, b];
}

/**
 * B1754: the maximum number of entries in `KineDBSocket.schemaCache`. The key
 * holds the SQL text, so each new literal adds one entry. Without a bound, a
 * long-lived connection grows for ever. Past the bound, the client drops the
 * least recently used entry. An evicted shape costs one FLAG_WANT_SCHEMA on
 * its next run and nothing more.
 */
export const SCHEMA_CACHE_CAPACITY = 1024;

/** Convenience: rows as `[{col:val, ...}]` objects instead of `[[val,...]]`. */
export function rowsAsObjects(rowsResult) {
  if (!rowsResult || rowsResult.type !== 'rows') return [];
  const { columns, data } = rowsResult;
  return data.map(row => Object.fromEntries(columns.map((c, i) => [c, row[i]])));
}
